import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, TrendingDown, ArrowRight, AlertCircle, Filter, Settings, DollarSign, Target, BarChart3, Percent, Clock, AlertTriangle, X, User, Activity, ChevronDown, ChevronRight, Check, Edit2, Search, Layers } from 'lucide-react';
import axios from 'axios';
import { normaliserObjectifs, objectifActionnable } from '../utils/objectifsCampagne';
import { ComposedChart, Line, Bar, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import config from '../config';
import AucunCompteGoogleAds from './connectors/AucunCompteGoogleAds';

const API_URL = config.API_URL;

// Composant input éditable avec état local pour permettre suppression complète
const EditableRecommendationInput = ({
  initialValue,
  recommendedValue,
  formatFn,
  onValueChange,
  isCustom
}) => {
  const [localValue, setLocalValue] = useState(initialValue);
  const isFocusedRef = React.useRef(false);
  const inputRef = React.useRef(null);

  // Synchroniser avec la valeur externe seulement quand pas en édition
  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(initialValue);
    }
  }, [initialValue]);

  const handleChange = (e) => {
    e.stopPropagation();
    const val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
    setLocalValue(val);
  };

  const handleFocus = (e) => {
    e.stopPropagation();
    isFocusedRef.current = true;
  };

  const handleBlur = (e) => {
    isFocusedRef.current = false;
    // Si vide, remettre la valeur recommandée
    const finalValue = localValue === '' ? formatFn(recommendedValue) : localValue;
    setLocalValue(finalValue);
    onValueChange(finalValue);
  };

  const hasCustomStyle = localValue !== '' && localValue !== formatFn(recommendedValue);

  return (
    <input
      ref={inputRef}
      type="text"
      value={localValue}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={{
        width: '65px',
        padding: '4px 6px',
        fontSize: '14px',
        fontWeight: '600',
        border: hasCustomStyle ? '2px solid #6366f1' : '1px solid #d1d5db',
        borderRadius: '4px',
        textAlign: 'center',
        backgroundColor: hasCustomStyle ? '#e0e7ff' : 'white',
        color: hasCustomStyle ? '#4338ca' : '#374151'
      }}
    />
  );
};

// Paramètres par défaut
const DEFAULT_SETTINGS = {
  period: 'LAST_30_DAYS',
  granularity: 'hierarchical',
  targetRoas: 3,
  targetCpa: 30,
  aggressiveness: 'moderate',
  campaignType: 'ALL',
  biddingStrategy: 'ALL',
  minConversions: 5, // Seuil minimum de conversions pour les recommandations
  minClicksNoConversion: 100, // Seuil de clics sans conversion pour alerte
  minCostNoConversion: 50 // Seuil de coût (€) sans conversion pour alerte
};

// Nom de l'outil pour la sauvegarde des paramètres
const TOOL_NAME = 'bidOptimizer';

const BidOptimizerTool = ({ token, selectedClientId, updateUrlWithAccount }) => {
  // États pour les comptes
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // États pour les données
  const [data, setData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // États pour les filtres et paramètres
  const [showFilters, setShowFilters] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [savedSettings, setSavedSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Charger les paramètres depuis l'API quand le compte change
  // Note: Les filtres de vue (granularity, campaignType, biddingStrategy) ne sont PAS chargés
  useEffect(() => {
    const loadSettings = async () => {
      if (!token || !selectedAccount) {
        setSettings(DEFAULT_SETTINGS);
        setSettingsLoaded(false);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/api/tools/settings/${TOOL_NAME}/${selectedAccount}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.success && response.data.settings) {
          // Ignorer les filtres de vue stockés (ils doivent toujours être réinitialisés)
          const { granularity, campaignType, biddingStrategy, ...loadedSettings } = response.data.settings;
          const newSettings = { ...DEFAULT_SETTINGS, ...loadedSettings };
          setSettings(newSettings);
          setSavedSettings(newSettings);
        } else {
          setSettings(DEFAULT_SETTINGS);
          setSavedSettings(DEFAULT_SETTINGS);
        }
      } catch (e) {
        console.error('Erreur chargement paramètres:', e);
        setSettings(DEFAULT_SETTINGS);
      } finally {
        setSettingsLoaded(true);
      }
    };
    loadSettings();
  }, [token, selectedAccount]);

  // Sauvegarder les paramètres dans l'API quand ils changent (après chargement initial)
  // Note: Les filtres de vue (granularity, campaignType, biddingStrategy) ne sont PAS sauvegardés
  useEffect(() => {
    if (!settingsLoaded || !token || !selectedAccount) return;

    const saveSettings = async () => {
      try {
        // Exclure les filtres de vue de la sauvegarde (ils seront réinitialisés à chaque visite)
        const { granularity, campaignType, biddingStrategy, ...settingsToSave } = settings;
        await axios.post(`${API_URL}/api/tools/settings/${TOOL_NAME}/${selectedAccount}`,
          { settings: settingsToSave },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } catch (e) {
        console.error('Erreur sauvegarde paramètres:', e);
      }
    };

    // Debounce pour éviter trop d'appels API
    const timeoutId = setTimeout(saveSettings, 500);
    return () => clearTimeout(timeoutId);
  }, [settings, settingsLoaded, token, selectedAccount]);

  // Vérifier si les paramètres sont personnalisés (différents des valeurs par défaut)
  const hasCustomSettings = useMemo(() => {
    return Object.keys(DEFAULT_SETTINGS).some(key => {
      const defaultVal = DEFAULT_SETTINGS[key];
      const currentVal = settings[key];
      return String(defaultVal) !== String(currentVal);
    });
  }, [settings]);

  // Vérifier si les paramètres ont été modifiés (non sauvegardés)
  const hasUnsavedChanges = useMemo(() => {
    return Object.keys(DEFAULT_SETTINGS).some(key => {
      return String(settings[key]) !== String(savedSettings[key]);
    });
  }, [settings, savedSettings]);

  // Filtres d'affichage
  const [recommendationFilter, setRecommendationFilter] = useState(null); // null, 'increase', 'decrease', etc.
  const [priorityFilter, setPriorityFilter] = useState(null); // null, 'high', 'medium', 'low'
  const [sortColumn, setSortColumn] = useState('priority');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal détails modification
  const [selectedChangeDetails, setSelectedChangeDetails] = useState(null);

  // Modal historique campagne
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignHistory, setCampaignHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [hoveredChangeDate, setHoveredChangeDate] = useState(null);

  // États pour la vue hiérarchique
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [adgroupsData, setAdgroupsData] = useState({}); // { campaignId: [adgroups] }
  const [assetGroupsData, setAssetGroupsData] = useState({}); // { campaignId: [assetGroups] } pour PMAX
  const [loadingAdgroups, setLoadingAdgroups] = useState(new Set());

  // États pour les groupes de produits (Shopping)
  const [expandedAdgroups, setExpandedAdgroups] = useState(new Set()); // Set de "campaignId-adGroupId"
  const [productGroupsData, setProductGroupsData] = useState({}); // { "campaignId-adGroupId": [productGroups] }
  const [loadingProductGroups, setLoadingProductGroups] = useState(new Set()); // Set de "campaignId-adGroupId"
  const [updatingProductGroups, setUpdatingProductGroups] = useState(new Set()); // Pour les actions en cours
  const [appliedProductGroups, setAppliedProductGroups] = useState(new Set()); // Set de "adGroupId-criterionId" pour les PG appliqués
  const [productGroupInputValues, setProductGroupInputValues] = useState({}); // { "adGroupId-criterionId": "valeur" }

  // États pour les mots-clés (modification CPC)
  const [updatingKeywords, setUpdatingKeywords] = useState(new Set()); // Pour les actions en cours
  const [appliedKeywords, setAppliedKeywords] = useState(new Set()); // Set de "adGroupId-criterionId" pour les keywords appliqués
  const [keywordInputValues, setKeywordInputValues] = useState({}); // { "adGroupId-criterionId": "valeur" }

  // États pour les mots-clés (Search)
  const [keywordsData, setKeywordsData] = useState({}); // { "campaignId-adGroupId": [keywords] }
  const [loadingKeywords, setLoadingKeywords] = useState(new Set()); // Set de "campaignId-adGroupId"

  // Vider le cache des groupes de produits et keywords quand la période ou l'agressivité change
  useEffect(() => {
    setProductGroupsData({});
    setKeywordsData({});
  }, [settings.period, settings.aggressiveness]);

  // États pour les objectifs personnalisés
  const [customObjectives, setCustomObjectives] = useState({});
  const [editingObjective, setEditingObjective] = useState(null); // { campaignId, campaignName, targetCpa, targetRoas }
  const [objectiveForm, setObjectiveForm] = useState({ type: 'CPA', cpaValue: '', roasValue: '' });
  const [savingObjective, setSavingObjective] = useState(false);

  // Valeurs calculées pour le modal d'objectif
  const objectiveModalValues = useMemo(() => {
    const currentValue = objectiveForm.type === 'CPA' ? objectiveForm.cpaValue : objectiveForm.roasValue;
    return {
      currentValue,
      hasValue: !!currentValue,
      isDisabled: !currentValue || savingObjective
    };
  }, [objectiveForm.type, objectiveForm.cpaValue, objectiveForm.roasValue, savingObjective]);

  // États pour les valeurs de recommandation personnalisées (Map: itemId -> valeur numérique)
  const [customRecommendationValues, setCustomRecommendationValues] = useState(new Map());

  // Fonctions pour gérer les valeurs personnalisées
  const getCustomRecommendationValue = (itemId) => {
    return customRecommendationValues.get(itemId);
  };

  const setCustomRecommendationValue = (itemId, value) => {
    setCustomRecommendationValues(prev => {
      const newMap = new Map(prev);
      if (value === null || value === undefined) {
        newMap.delete(itemId);
      } else {
        // Garder la valeur même si c'est une chaîne vide (pour permettre l'édition)
        newMap.set(itemId, value);
      }
      return newMap;
    });
  };

  const resetCustomRecommendationValue = (itemId) => {
    setCustomRecommendationValues(prev => {
      const newMap = new Map(prev);
      newMap.delete(itemId);
      return newMap;
    });
  };

  // États pour l'application des recommandations
  const [applyingRecommendations, setApplyingRecommendations] = useState(new Set());
  const [appliedRecommendations, setAppliedRecommendations] = useState(new Set());
  const [appliedValues, setAppliedValues] = useState(new Map()); // Map: key -> valeur appliquée
  const [applyError, setApplyError] = useState(null);

  // Fonction pour appliquer une recommandation dans Google Ads
  const applyRecommendation = async (item, customValue, strategyType, updateType) => {
    // Pour les ad groups, utiliser item.id (ID de l'ad group), pas item.campaignId
    const entityType = item.level || 'campaign';
    const itemId = entityType === 'adgroup' ? item.id : (item.campaignId || item.id);
    const key = `${itemId}-${updateType}`;

    // Vérifier si déjà en cours
    if (applyingRecommendations.has(key)) {
      return;
    }

    // Alerte pour les modifications CPC au niveau ad group
    if (entityType === 'adgroup' && updateType === 'cpcBid') {
      const confirmMessage = `⚠️ Attention : Modification du CPC au niveau groupe d'annonces

La modification du CPC d'un groupe d'annonces ne s'appliquera qu'aux mots-clés qui n'ont PAS de CPC défini individuellement.

Les mots-clés avec un CPC personnalisé conserveront leur propre enchère.

Voulez-vous continuer ?`;

      if (!window.confirm(confirmMessage)) {
        return; // L'utilisateur a annulé
      }
    }

    setApplyingRecommendations(prev => new Set([...prev, key]));
    setApplyError(null);

    try {
      // Déterminer la valeur à appliquer
      const valueToApply = customValue !== null && customValue !== undefined && customValue !== ''
        ? parseFloat(customValue)
        : null;

      if (valueToApply === null || isNaN(valueToApply)) {
        throw new Error('Valeur invalide');
      }

      console.log(`💰 [APPLY] Envoi: ${item.level || 'campaign'} ${itemId}, ${updateType}: ${valueToApply}`);

      const response = await axios.post(
        `${API_URL}/api/tools/bid-optimizer/apply`,
        {
          clientId: selectedAccount,
          entityId: itemId,
          entityType: item.level || 'campaign',
          updateType,
          newValue: valueToApply,
          strategyType,
          entityName: item.name
        },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 120000 // 120s pour les opérations Google Ads (peut être lent)
        }
      );

      if (response.data.success) {
        console.log(`✅ [APPLY] Succès:`, response.data.message);
        setAppliedRecommendations(prev => new Set([...prev, key]));

        // Stocker la valeur appliquée pour l'afficher dans l'input
        setAppliedValues(prev => {
          const newMap = new Map(prev);
          newMap.set(key, valueToApply);
          return newMap;
        });

        // Notification de succès
        const entityName = item.name || response.data.entityName || 'Campagne';
        alert(`Modification appliquée avec succès !\n\n${entityName}\n${response.data.message}`);

        // NE PAS rafraîchir automatiquement - garder la valeur appliquée visible
      } else {
        throw new Error(response.data.error || 'Erreur inconnue');
      }

    } catch (error) {
      console.error(`❌ [APPLY] Erreur:`, error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de l\'application';
      setApplyError(`${item.name}: ${errorMessage}`);
      alert(`Erreur lors de l'application:\n\n${errorMessage}`);
    } finally {
      setApplyingRecommendations(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  // Charger les comptes au montage et quand le client change
  useEffect(() => {
    if (token) {
      // Réinitialiser tous les états quand le client change
      setSelectedAccount('');
      setData([]);
      setStats(null);
      setError(null);
      setSettingsLoaded(false);
      setSelectedCampaign(null);
      setCampaignHistory(null);
      // Recharger les comptes
      loadAccounts();
    }
  }, [token, selectedClientId]);

  // Charger les données quand le compte est sélectionné ET que les paramètres sont chargés
  useEffect(() => {
    if (token && selectedAccount && settingsLoaded) {
      fetchData();
      fetchCustomObjectives();
    }
  }, [token, selectedAccount, settingsLoaded]);

  // Pré-charger tous les adgroups en arrière-plan après le chargement initial des campagnes
  useEffect(() => {
    if (!data.length) return;

    // Fonction pour pré-charger tous les adgroups
    const prefetchAllAdgroups = async () => {
      // Filtrer les campagnes non-PMAX qui n'ont pas encore leurs adgroups chargés
      const campaignsToFetch = data.filter(item => {
        const isPmax = item.type === 'PERFORMANCE_MAX' || item.type === 'PMAX' || item.type === 10 || item.type === '10';
        const campaignId = item.id || item.campaignId;
        const notAlreadyLoaded = !adgroupsData[campaignId];
        return !isPmax && notAlreadyLoaded;
      });

      // Charger en parallèle par lots de 3
      for (let i = 0; i < campaignsToFetch.length; i += 3) {
        const batch = campaignsToFetch.slice(i, i + 3);
        await Promise.all(batch.map(campaign => {
          const campaignId = campaign.id || campaign.campaignId;
          if (!adgroupsData[campaignId] && !loadingAdgroups.has(campaignId)) {
            return fetchAdgroups(campaignId);
          }
          return Promise.resolve();
        }));
      }
    };

    // Délai pour ne pas bloquer le rendu initial
    const timer = setTimeout(prefetchAllAdgroups, 500);
    return () => clearTimeout(timer);
  }, [data]);

  // Fonction pour charger la liste des comptes
  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      console.log('🔄 [BID-OPTIMIZER] Chargement des comptes Google Ads...');

      const response = await axios.get(`${API_URL}/api/library?usage=outils`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success && response.data.library) {
        const data = response.data;

        const googleSheetsItems = data.library?.GOOGLE_SHEETS || [];
        const googleAdsItems = data.library?.GOOGLE_ADS || [];

        const adsItems = [
          ...googleSheetsItems.filter(item => item.type === 'GOOGLE_ADS'),
          ...googleAdsItems.filter(item => item.type === 'GOOGLE_ADS')
        ];

        console.log(`📊 [BID-OPTIMIZER] ${adsItems.length} compte(s) Google Ads trouvé(s)`);

        let filteredAccounts = adsItems;
        if (selectedClientId) {
          filteredAccounts = await filterAccountsByClient(adsItems, selectedClientId);
        }

        // Trier les comptes par ordre alphabétique
        const sortedAccounts = [...filteredAccounts].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
        );
        setAccounts(sortedAccounts);

        if (sortedAccounts.length === 1) {
          const firstAccount = sortedAccounts[0];
          const accountId = firstAccount.sourceId || firstAccount.id;
          setSelectedAccount(accountId);
        }
      } else {
        setError('Format de réponse inattendu pour les comptes');
      }
    } catch (error) {
      console.error('❌ [BID-OPTIMIZER] Erreur chargement comptes:', error);
      setError('Impossible de charger les comptes Google Ads: ' + error.message);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Filtrer les comptes selon le client sélectionné
  const filterAccountsByClient = async (accounts, clientId) => {
    if (!clientId) return accounts;

    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success && data.sites) {
        const clientSites = data.sites.filter(site => site.clientId === clientId);
        const authorizedAccountIds = clientSites.flatMap(site => site.accountIds || []);

        const filteredAccounts = accounts.filter(account => {
          const matchesId = authorizedAccountIds.includes(account.id);
          const matchesSourceId = authorizedAccountIds.includes(account.sourceId);
          return matchesId || matchesSourceId;
        });

        return filteredAccounts;
      }
    } catch (err) {
      console.error('❌ [BID-OPTIMIZER] Erreur filtrage comptes:', err);
    }

    return accounts;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setAdgroupsData({}); // Réinitialiser les adgroups pour forcer le rechargement

    try {
      // Toujours charger au niveau campagne, les adgroups sont chargés séparément
      const response = await axios.get(`${API_URL}/api/tools/bid-optimizer`, {
        params: {
          clientId: selectedAccount,
          period: settings.period,
          granularity: 'campaign',
          targetRoas: settings.targetRoas,
          targetCpa: settings.targetCpa,
          aggressiveness: settings.aggressiveness,
          campaignType: settings.campaignType,
          minConversions: settings.minConversions,
          minClicksNoConversion: settings.minClicksNoConversion,
          minCostNoConversion: settings.minCostNoConversion
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setData(response.data.data);
        setStats(response.data.stats);
        console.log(`✅ [BID-OPTIMIZER] ${response.data.data.length} items récupérés`);
        // Debug: afficher les données de stratégie portfolio pour les premiers items
        const itemsWithPortfolio = response.data.data.filter(d => d.isPortfolioStrategy);
        console.log(`📊 [BID-OPTIMIZER] ${itemsWithPortfolio.length} items avec stratégie portfolio`);
        if (itemsWithPortfolio.length > 0) {
          console.log(`📊 [BID-OPTIMIZER] Exemple portfolio:`, itemsWithPortfolio.slice(0, 3).map(d => ({
            name: d.name || d.campaignName,
            isPortfolioStrategy: d.isPortfolioStrategy,
            portfolioName: d.portfolioStrategy?.name,
            targetValue: d.portfolioStrategy?.targetValue,
            maxBidLimit: d.portfolioStrategy?.maxBidLimit,
            type: d.portfolioStrategy?.type
          })));
        }
      } else {
        setError(response.data.error || 'Erreur lors de la récupération des données');
      }
    } catch (err) {
      console.error('❌ [BID-OPTIMIZER] Erreur:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Charger l'historique d'une campagne
  const fetchCampaignHistory = async (campaign) => {
    // Normaliser les données pour supporter les deux formats (hiérarchique et classique)
    const normalizedCampaign = {
      ...campaign,
      id: campaign.id || campaign.campaignId,
      name: campaign.name || campaign.campaignName
    };
    setSelectedCampaign(normalizedCampaign);
    setLoadingHistory(true);
    setCampaignHistory(null);

    const campaignId = campaign.id || campaign.campaignId;
    try {
      const response = await axios.get(`${API_URL}/api/tools/bid-optimizer/campaign-history/${campaignId}`, {
        params: { clientId: selectedAccount },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setCampaignHistory(response.data.data);
        console.log(`✅ [CAMPAIGN-HISTORY] ${response.data.data.length} jours récupérés`);
      }
    } catch (err) {
      console.error('❌ [CAMPAIGN-HISTORY] Erreur:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Charger les objectifs personnalisés
  const fetchCustomObjectives = async () => {
    if (!token || !selectedAccount) return;
    try {
      const response = await axios.get(`${API_URL}/api/tools/campaign-objectives`, {
        params: { accountId: selectedAccount },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        /**
         * L'API rend désormais **une liste** d'objectifs par campagne : une campagne peut viser un
         * CPA, un ROAS, un CPC et un volume de conversions à la fois. Cet outil n'en retient qu'un,
         * et seulement parmi les deux qu'il sait interpréter — `objectifActionnable` filtre et
         * arbitre (CPA prioritaire sur ROAS), exactement comme le fait le serveur.
         *
         * `normaliserObjectifs` accepte au passage l'ancienne forme (un objet par campagne) : le
         * frontend et le backend ne se déploient pas à la même seconde.
         */
        const parCampagne = normaliserObjectifs(response.data.objectives || {});
        const retenus = {};
        Object.entries(parCampagne).forEach(([campaignId, objectifs]) => {
          const retenu = objectifActionnable(objectifs);
          if (retenu) retenus[campaignId] = retenu;
        });
        setCustomObjectives(retenus);
      }
    } catch (err) {
      console.error('Erreur chargement objectifs personnalisés:', err);
    }
  };

  // Sauvegarder un objectif personnalisé
  const saveCustomObjective = async () => {
    const currentValue = objectiveForm.type === 'CPA' ? objectiveForm.cpaValue : objectiveForm.roasValue;
    if (!editingObjective || !currentValue) return;

    setSavingObjective(true);
    try {
      const response = await axios.post(`${API_URL}/api/tools/campaign-objectives`, {
        accountId: selectedAccount,
        campaignId: editingObjective.campaignId,
        objectiveType: objectiveForm.type,
        objectiveValue: parseFloat(currentValue)
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        /**
         * Relire au lieu de deviner.
         *
         * Une campagne peut porter plusieurs objectifs : enregistrer un ROAS n'efface plus le CPA
         * qui existait. Or c'est le CPA qui reste retenu par l'outil. Une mise à jour optimiste
         * afficherait donc le ROAS qu'on vient de saisir, puis basculerait sur le CPA au
         * rechargement suivant — mieux vaut afficher tout de suite ce qui sera effectivement
         * appliqué.
         */
        await fetchCustomObjectives();
        setEditingObjective(null);
        setObjectiveForm({ type: 'CPA', cpaValue: '', roasValue: '' });
        // Recharger les données pour recalculer les recommandations avec le nouvel objectif
        fetchData();
      }
    } catch (err) {
      console.error('Erreur sauvegarde objectif:', err);
      alert('Erreur lors de la sauvegarde de l\'objectif');
    } finally {
      setSavingObjective(false);
    }
  };

  // Supprimer un objectif personnalisé
  const deleteCustomObjective = async (campaignId) => {
    /**
     * Le type fait partie du chemin, et ce n'est pas un raffinement.
     *
     * Une campagne peut porter plusieurs objectifs ; l'ancienne route, sans type, les aurait tous
     * désactivés d'un coup — un objectif de conversions aurait disparu parce qu'on supprimait un
     * CPA. On ne supprime donc que celui qui est affiché ici.
     */
    const objectifAffiche = customObjectives[campaignId];
    if (!objectifAffiche) return;

    if (!window.confirm(`Supprimer l'objectif ${objectifAffiche.objectiveType} personnalisé ? Les recommandations utiliseront à nouveau l'objectif Google Ads.`)) return;

    try {
      const response = await axios.delete(`${API_URL}/api/tools/campaign-objectives/${campaignId}/${objectifAffiche.objectiveType}`, {
        params: { accountId: selectedAccount },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setCustomObjectives(prev => {
          const newObj = { ...prev };
          delete newObj[campaignId];
          return newObj;
        });
        // Recharger les données pour recalculer les recommandations
        fetchData();
      }
    } catch (err) {
      console.error('Erreur suppression objectif:', err);
    }
  };

  // Ouvrir le modal d'édition d'objectif
  const openObjectiveEditor = (campaignId, campaignName, targetCpa, targetRoas) => {
    const customObj = customObjectives[campaignId];

    // Initialiser les deux valeurs (CPA et ROAS) séparément
    let cpaValue = targetCpa ? targetCpa.toFixed(2) : '';
    let roasValue = targetRoas ? (targetRoas * 100).toFixed(0) : '';
    let activeType = 'CPA';

    if (customObj) {
      // Si un objectif personnalisé existe, l'utiliser
      if (customObj.objectiveType === 'CPA') {
        cpaValue = customObj.objectiveValue.toString();
        activeType = 'CPA';
      } else {
        roasValue = customObj.objectiveValue.toString();
        activeType = 'ROAS';
      }
    } else if (targetRoas && !targetCpa) {
      // Si seulement ROAS est défini, sélectionner ROAS
      activeType = 'ROAS';
    }

    setObjectiveForm({ type: activeType, cpaValue, roasValue });
    setEditingObjective({ campaignId, campaignName, targetCpa, targetRoas });
  };

  // Filtrer et trier les données
  // Fonction pour déterminer la couleur effective de la recommandation (prend en compte l'inversion ROAS)
  // parentCampaign: optionnel, utilisé pour les adgroups afin d'hériter du type de stratégie de la campagne parente
  const getEffectiveRecommendationColor = (item, parentCampaign = null) => {
    const recommendation = item.recommendation;
    if (!recommendation || recommendation === 'maintain' || recommendation === 'test') {
      return recommendation;
    }

    // budget et increase_max_cpc sont toujours verts (comme increase)
    if (recommendation === 'budget' || recommendation === 'increase_max_cpc') {
      return 'increase';
    }

    // Pour les adgroups, utiliser la stratégie de la campagne parente
    const isAdgroup = item.level === 'adgroup';
    const strategySource = (isAdgroup && parentCampaign) ? parentCampaign : item;

    // Détecter le type de stratégie
    const biddingStr = typeof strategySource.biddingStrategy === 'string' ? strategySource.biddingStrategy : '';
    const biddingLabel = typeof strategySource.biddingStrategyLabel === 'string' ? strategySource.biddingStrategyLabel : '';

    const isRoasStrategy = strategySource.targetRoas ||
      biddingStr.includes('ROAS') || biddingLabel.includes('ROAS') ||
      strategySource.biddingStrategy === 8 || strategySource.biddingStrategy === 11;

    // Détecter stratégie CPA
    const biddingNum = parseInt(strategySource.biddingStrategy, 10);
    const portfolioTypeNum = parseInt(strategySource.portfolioStrategy?.type, 10);
    const isCpaStrategy = strategySource.targetCpa ||
      biddingStr.includes('CPA') || biddingLabel.includes('CPA') ||
      biddingNum === 2 || biddingNum === 10 ||
      portfolioTypeNum === 2 || portfolioTypeNum === 10;

    // Détecter stratégie avec CPC max (portfolio avec maxBidLimit OU taux d'impression cible avec CPC max)
    // Ne s'applique pas aux adgroups (CPC max = niveau campagne/portfolio uniquement)
    const isTargetImpressionShare = biddingNum === 15 || biddingStr === 'TARGET_IMPRESSION_SHARE';
    const isCpcMaxStrategy = !isAdgroup && (
      strategySource.portfolioStrategy?.maxBidLimit > 0 ||
      (isTargetImpressionShare && strategySource.targetImpressionShareMaxCpc > 0)
    );

    // Pour ROAS: les couleurs sont inversées
    // (Augmenter ROAS = restrict = rouge, Baisser ROAS = expand = vert)
    if (isRoasStrategy) {
      if (recommendation === 'increase') return 'decrease'; // Augmenter ROAS = rouge = restrict
      if (recommendation === 'decrease') return 'increase'; // Baisser ROAS = vert = expand
    }

    // Pour CPA: les couleurs sont inversées
    // (Augmenter CPA cible = mauvaises perfs = rouge, Baisser CPA cible = bonnes perfs = vert)
    if (isCpaStrategy) {
      if (recommendation === 'increase') return 'decrease'; // Augmenter CPA = rouge = mauvaises perfs
      if (recommendation === 'decrease') return 'increase'; // Baisser CPA = vert = bonnes perfs
    }

    // Pour CPC_MAX: les couleurs sont aussi inversées
    // (Baisser CPC max = restrict = rouge, Augmenter CPC max = expand = vert)
    if (isCpcMaxStrategy) {
      if (recommendation === 'increase') return 'decrease'; // Baisser CPC max = rouge = restrict
      if (recommendation === 'decrease') return 'increase'; // Augmenter CPC max = vert = expand
    }

    return recommendation;
  };

  // Charger les adgroups d'une campagne
  const fetchAdgroups = async (campaignId) => {
    if (adgroupsData[campaignId] || loadingAdgroups.has(campaignId)) return;

    setLoadingAdgroups(prev => new Set([...prev, campaignId]));

    try {
      const response = await axios.get(`${API_URL}/api/tools/bid-optimizer`, {
        params: {
          clientId: selectedAccount,
          period: settings.period,
          granularity: 'adgroup',
          targetRoas: settings.targetRoas,
          targetCpa: settings.targetCpa,
          aggressiveness: settings.aggressiveness,
          campaignType: settings.campaignType,
          campaignId: campaignId, // Filtrer par campagne
          minConversions: settings.minConversions,
          minClicksNoConversion: settings.minClicksNoConversion,
          minCostNoConversion: settings.minCostNoConversion
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        // Filtrer les adgroups de cette campagne
        const campaignAdgroups = response.data.data.filter(item =>
          item.campaignId === campaignId || item.campaignId === String(campaignId)
        );
        console.log('🔍 [BID-OPTIMIZER] Adgroups reçus:', campaignAdgroups.map(ag => ({
          name: ag.name,
          status: ag.status,
          impressionShare: ag.impressionShare,
          campaignType: ag.campaignType
        })));
        setAdgroupsData(prev => ({
          ...prev,
          [campaignId]: campaignAdgroups
        }));
      }
    } catch (err) {
      console.error('❌ [BID-OPTIMIZER] Erreur chargement adgroups:', err);
    } finally {
      setLoadingAdgroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(campaignId);
        return newSet;
      });
    }
  };

  // Charger les asset groups d'une campagne PMAX
  const fetchAssetGroups = async (campaignId, campaignName) => {
    console.log('🎭 [BID-OPTIMIZER] fetchAssetGroups appelé:', { campaignId, campaignName, selectedAccount });

    if (assetGroupsData[campaignId] || loadingAdgroups.has(campaignId)) {
      console.log('🎭 [BID-OPTIMIZER] fetchAssetGroups ignoré (déjà chargé ou en cours)');
      return;
    }

    setLoadingAdgroups(prev => new Set([...prev, campaignId]));

    try {
      console.log('🎭 [BID-OPTIMIZER] Appel API asset-groups...');
      const response = await axios.post(`${API_URL}/api/dashboard/components/asset-groups`, {
        sources: [selectedAccount],
        period: settings.period,
        campaignFilter: { id: campaignId, name: campaignName }
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('🎭 [BID-OPTIMIZER] Réponse API asset-groups:', response.data);

      if (response.data.success && response.data.data?.assetGroups) {
        console.log('🔍 [BID-OPTIMIZER] Asset groups reçus:', response.data.data.assetGroups.map(ag => ({ name: ag.name, status: ag.status })));
        setAssetGroupsData(prev => ({
          ...prev,
          [campaignId]: response.data.data.assetGroups
        }));
      } else {
        console.log('⚠️ [BID-OPTIMIZER] Pas de données asset groups dans la réponse:', response.data);
        setAssetGroupsData(prev => ({
          ...prev,
          [campaignId]: []
        }));
      }
    } catch (err) {
      console.error('❌ [BID-OPTIMIZER] Erreur chargement asset groups:', err);
      // Mettre un tableau vide pour éviter de retenter
      setAssetGroupsData(prev => ({
        ...prev,
        [campaignId]: []
      }));
    } finally {
      setLoadingAdgroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(campaignId);
        return newSet;
      });
    }
  };

  // ============================================================================
  // FONCTIONS POUR LES GROUPES DE PRODUITS (SHOPPING)
  // ============================================================================

  /**
   * Récupérer les groupes de produits pour un groupe d'annonces Shopping
   */
  const fetchProductGroups = async (campaignId, adGroupId) => {
    const key = `${campaignId}-${adGroupId}`;

    if (productGroupsData[key] || loadingProductGroups.has(key)) return;

    setLoadingProductGroups(prev => new Set([...prev, key]));

    try {
      const response = await axios.get(`${API_URL}/api/tools/bid-optimizer/product-groups`, {
        params: {
          clientId: selectedAccount,
          campaignId,
          adGroupId,
          period: settings.period,
          aggressiveness: settings.aggressiveness,
          minConversions: settings.minConversions,
          minClicksNoConversion: settings.minClicksNoConversion,
          minCostNoConversion: settings.minCostNoConversion
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log(`📦 [BID-OPTIMIZER] Product groups reçus pour ${key}:`, response.data.data.length);
        setProductGroupsData(prev => ({
          ...prev,
          [key]: response.data.data
        }));
      }
    } catch (err) {
      console.error('❌ [BID-OPTIMIZER] Erreur chargement product groups:', err);
      setProductGroupsData(prev => ({
        ...prev,
        [key]: []
      }));
    } finally {
      setLoadingProductGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  /**
   * Récupérer les mots-clés pour un groupe d'annonces Search
   * @param {string} biddingStrategy - Stratégie d'enchères de la campagne (pour savoir si on peut modifier le CPC)
   */
  const fetchKeywords = async (campaignId, adGroupId, biddingStrategy = null) => {
    const key = `${campaignId}-${adGroupId}`;

    if (keywordsData[key] || loadingKeywords.has(key)) return;

    setLoadingKeywords(prev => new Set([...prev, key]));

    try {
      const response = await axios.get(`${API_URL}/api/tools/bid-optimizer/keywords`, {
        params: {
          clientId: selectedAccount,
          campaignId,
          adGroupId,
          period: settings.period,
          targetRoas: settings.targetRoas,
          targetCpa: settings.targetCpa,
          aggressiveness: settings.aggressiveness,
          biddingStrategy: biddingStrategy,
          minConversions: settings.minConversions,
          minClicksNoConversion: settings.minClicksNoConversion,
          minCostNoConversion: settings.minCostNoConversion
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log(`🔑 [BID-OPTIMIZER] Keywords reçus pour ${key}:`, response.data.data.length);
        setKeywordsData(prev => ({
          ...prev,
          [key]: response.data.data
        }));
      }
    } catch (err) {
      console.error('❌ [BID-OPTIMIZER] Erreur chargement keywords:', err);
      setKeywordsData(prev => ({
        ...prev,
        [key]: []
      }));
    } finally {
      setLoadingKeywords(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  /**
   * Modifier le CPC d'un mot-clé
   */
  const updateKeywordCpc = async (adGroupId, criterionId, newCpc, keywordText = 'Mot-clé') => {
    const key = `kw-${adGroupId}-${criterionId}`;

    setUpdatingKeywords(prev => new Set([...prev, key]));

    try {
      const response = await axios.post(`${API_URL}/api/tools/bid-optimizer/keywords/update-cpc`, {
        clientId: selectedAccount,
        adGroupId,
        criterionId,
        newCpc
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log(`✅ [BID-OPTIMIZER] CPC keyword modifié: ${criterionId} -> ${newCpc}€`);
        // Marquer comme appliqué
        setAppliedKeywords(prev => new Set([...prev, `${adGroupId}-${criterionId}`]));
        // Alert de succès
        alert(`Modification appliquée avec succès !\n\n${keywordText}\nNouveau CPC : ${newCpc}€`);
      } else {
        throw new Error(response.data.error || 'Erreur lors de la modification');
      }
    } catch (err) {
      console.error('❌ [BID-OPTIMIZER] Erreur modification CPC keyword:', err);
      alert('Erreur lors de la modification du CPC: ' + (err.response?.data?.error || err.message));
    } finally {
      setUpdatingKeywords(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  /**
   * Toggle expansion d'un groupe d'annonces pour afficher les groupes de produits ou mots-clés
   */
  const toggleAdgroupExpand = (campaignId, adGroupId, campaignType, biddingStrategy = null) => {
    const key = `${campaignId}-${adGroupId}`;
    const isCurrentlyExpanded = expandedAdgroups.has(key);

    if (isCurrentlyExpanded) {
      // Replier
      setExpandedAdgroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    } else {
      // Déplier
      setExpandedAdgroups(prev => {
        const newSet = new Set(prev);
        newSet.add(key);
        return newSet;
      });

      // Charger les données selon le type de campagne
      const isShopping = campaignType === 'SHOPPING' || campaignType === 4 || campaignType === '4';
      const isSearch = campaignType === 'SEARCH' || campaignType === 2 || campaignType === '2';

      if (isShopping && !productGroupsData[key]) {
        // Charger les groupes de produits pour Shopping
        fetchProductGroups(campaignId, adGroupId);
      } else if (isSearch && !keywordsData[key]) {
        // Charger les mots-clés pour Search (passer la stratégie pour savoir si CPC modifiable)
        fetchKeywords(campaignId, adGroupId, biddingStrategy);
      }
    }
  };

  /**
   * Modifier l'enchère d'un groupe de produits
   */
  const updateProductGroupBid = async (adGroupId, criterionId, newBid, productGroupName = 'Groupe de produits') => {
    const key = `pg-${adGroupId}-${criterionId}`;

    setUpdatingProductGroups(prev => new Set([...prev, key]));

    try {
      const response = await axios.post(`${API_URL}/api/tools/bid-optimizer/product-groups/update-bid`, {
        clientId: selectedAccount,
        adGroupId,
        criterionId,
        newBid
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log(`✅ [BID-OPTIMIZER] Enchère product group modifiée: ${criterionId} -> ${newBid}€`);
        // Mettre à jour le state local
        setProductGroupsData(prev => {
          const newData = { ...prev };
          for (const pgKey of Object.keys(newData)) {
            newData[pgKey] = newData[pgKey].map(pg =>
              pg.criterionId === criterionId ? { ...pg, cpcBid: parseFloat(newBid) } : pg
            );
          }
          return newData;
        });
        // Marquer comme appliqué
        const appliedKey = `${adGroupId}-${criterionId}`;
        setAppliedProductGroups(prev => new Set([...prev, appliedKey]));

        // Notification de succès
        alert(`Modification appliquée avec succès !\n\n${productGroupName}\nNouveau CPC : ${newBid}€`);

        return true;
      }
    } catch (err) {
      console.error('❌ [BID-OPTIMIZER] Erreur modification enchère product group:', err);
      alert('Erreur lors de la modification de l\'enchère: ' + (err.response?.data?.error || err.message));
    } finally {
      setUpdatingProductGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
    return false;
  };

  /**
   * Exclure un groupe de produits
   */
  const excludeProductGroup = async (adGroupId, criterionId, campaignId) => {
    const key = `pg-${adGroupId}-${criterionId}`;
    const pgKey = `${campaignId}-${adGroupId}`;

    if (!window.confirm('Voulez-vous vraiment exclure ce groupe de produits ?')) {
      return false;
    }

    setUpdatingProductGroups(prev => new Set([...prev, key]));

    try {
      const response = await axios.post(`${API_URL}/api/tools/bid-optimizer/product-groups/exclude`, {
        clientId: selectedAccount,
        adGroupId,
        criterionId
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log(`✅ [BID-OPTIMIZER] Product group exclu: ${criterionId}`);
        // Mettre à jour le state local
        setProductGroupsData(prev => {
          const newData = { ...prev };
          if (newData[pgKey]) {
            newData[pgKey] = newData[pgKey].map(pg =>
              pg.criterionId === criterionId ? { ...pg, status: 'EXCLUDED' } : pg
            );
          }
          return newData;
        });
        return true;
      }
    } catch (err) {
      console.error('❌ [BID-OPTIMIZER] Erreur exclusion product group:', err);
      alert('Erreur lors de l\'exclusion: ' + (err.response?.data?.error || err.message));
    } finally {
      setUpdatingProductGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
    return false;
  };

  /**
   * Obtenir l'icône pour un type de subdivision
   */
  const getSubdivisionIcon = (type) => {
    const icons = {
      'ITEM_ID': '🏷️',
      'BRAND': '🏢',
      'CATEGORY': '📁',
      'PRODUCT_TYPE': '📦',
      'CONDITION': '✨',
      'CHANNEL': '🌐',
      'CUSTOM_LABEL_0': '🔖',
      'CUSTOM_LABEL_1': '🔖',
      'CUSTOM_LABEL_2': '🔖',
      'CUSTOM_LABEL_3': '🔖',
      'CUSTOM_LABEL_4': '🔖',
      'ALL_PRODUCTS': '📋',
      'OTHER': '📌'
    };
    return icons[type] || '📌';
  };

  // Toggle expansion d'une campagne
  const toggleCampaignExpand = (campaignId, campaignType, campaignName) => {
    console.log('🔄 [BID-OPTIMIZER] Toggle expand:', { campaignId, campaignType, campaignName });

    const isCurrentlyExpanded = expandedCampaigns.has(campaignId);

    if (isCurrentlyExpanded) {
      // Replier
      setExpandedCampaigns(prev => {
        const newSet = new Set(prev);
        newSet.delete(campaignId);
        return newSet;
      });
    } else {
      // Déplier
      setExpandedCampaigns(prev => {
        const newSet = new Set(prev);
        newSet.add(campaignId);
        return newSet;
      });

      // Charger les données si pas déjà chargées
      const isPmax = campaignType === 'PERFORMANCE_MAX' || campaignType === 'PMAX' || campaignType === 10 || campaignType === '10';
      console.log('🔄 [BID-OPTIMIZER] isPmax:', isPmax, 'campaignType:', campaignType, 'typeof:', typeof campaignType);

      if (isPmax) {
        if (!assetGroupsData[campaignId]) {
          console.log('🔄 [BID-OPTIMIZER] Appel fetchAssetGroups pour:', campaignId);
          fetchAssetGroups(campaignId, campaignName);
        }
      } else {
        if (!adgroupsData[campaignId]) {
          console.log('🔄 [BID-OPTIMIZER] Appel fetchAdgroups pour:', campaignId);
          fetchAdgroups(campaignId);
        }
      }
    }
  };

  // Données hiérarchiques (campagnes avec leurs adgroups)
  const hierarchicalData = useMemo(() => {
    if (settings.granularity !== 'hierarchical') return [];

    // Grouper les données par campagne
    const campaignMap = new Map();

    data.forEach(item => {
      const campaignId = item.campaignId || item.id;
      const campaignName = item.campaignName || item.name;

      if (!campaignMap.has(campaignId)) {
        campaignMap.set(campaignId, {
          campaignId,
          campaignName,
          campaignType: item.type,
          biddingStrategy: item.biddingStrategy,
          biddingStrategyLabel: item.biddingStrategyLabel,
          isPortfolioStrategy: item.isPortfolioStrategy,
          portfolioStrategy: item.portfolioStrategy,
          targetRoas: item.targetRoas,
          targetCpa: item.targetCpa,
          targetImpressionShareMaxCpc: item.targetImpressionShareMaxCpc,
          budgetAmount: item.budgetAmount,
          hasRecommendedBudget: item.hasRecommendedBudget,
          // Métriques agrégées (on les calcule à partir des adgroups si disponibles)
          impressions: item.impressions || 0,
          clicks: item.clicks || 0,
          cost: item.cost || 0,
          conversions: item.conversions || 0,
          conversionsValue: item.conversionsValue || 0,
          cpa: item.cpa || 0,
          roas: item.roas || 0,
          impressionShare: item.impressionShare || 0,
          lostISRank: item.lostISRank || 0,
          // Recommandation campagne
          recommendation: item.recommendation,
          adjustmentRange: item.adjustmentRange,
          reason: item.reason,
          priority: item.priority,
          daysSinceLastChange: item.daysSinceLastChange,
          lastBidChangeDate: item.lastBidChangeDate,
          changeHistory: item.changeHistory,
          level: item.level,
          adgroups: []
        });
      }
    });

    return Array.from(campaignMap.values());
  }, [data, settings.granularity]);

  // Aplatir tous les adgroups chargés pour la vue "Par groupe d'annonces"
  const flattenedAdgroups = useMemo(() => {
    const allAdgroups = [];
    Object.entries(adgroupsData).forEach(([campaignId, adgroups]) => {
      if (Array.isArray(adgroups)) {
        adgroups.forEach(adgroup => {
          allAdgroups.push({
            ...adgroup,
            campaignId,
            // S'assurer que les champs nécessaires sont présents
            id: adgroup.id || adgroup.adGroupId,
            name: adgroup.name || adgroup.adGroupName,
          });
        });
      }
    });
    return allAdgroups;
  }, [adgroupsData]);

  const filteredData = useMemo(() => {
    // Pour la vue adgroup, utiliser les adgroups pré-chargés
    let result = settings.granularity === 'adgroup'
      ? [...flattenedAdgroups]
      : [...data];

    // Pour la vue campagne, exclure les données qui ne sont pas des campagnes
    if (settings.granularity === 'campaign') {
      result = result.filter(item => item.level === 'campaign' || !item.level);
    }

    // Filtre par type de campagne
    const effectiveCampaignType = settings.campaignType;
    if (effectiveCampaignType && effectiveCampaignType !== 'ALL') {
      result = result.filter(item => {
        const itemType = item.type || item.campaignType;
        // Mapper les codes numériques vers les noms de types
        const typeMap = {
          2: 'SEARCH', 3: 'DISPLAY', 4: 'SHOPPING', 5: 'HOTEL', 6: 'VIDEO',
          7: 'MULTI_CHANNEL', 8: 'LOCAL', 9: 'SMART', 10: 'PERFORMANCE_MAX',
          11: 'LOCAL_SERVICES', 12: 'DISCOVERY', 13: 'TRAVEL', 14: 'DEMAND_GEN'
        };
        const typeName = typeof itemType === 'number' ? typeMap[itemType] : itemType;
        return typeName === effectiveCampaignType;
      });
    }

    // Filtre par stratégie d'enchères
    if (settings.biddingStrategy && settings.biddingStrategy !== 'ALL') {
      result = result.filter(item => {
        const strategy = item.biddingStrategy;
        const portfolioType = item.portfolioStrategy?.type;
        const isPortfolio = item.isPortfolioStrategy === true;

        // Mapper les codes numériques vers les noms de stratégies
        const strategyMap = {
          2: 'ENHANCED_CPC',
          3: 'MANUAL_CPC',
          6: 'TARGET_CPA',
          8: 'TARGET_ROAS',
          9: 'TARGET_SPEND',
          10: 'MAXIMIZE_CONVERSIONS',
          11: 'MAXIMIZE_CONVERSION_VALUE',
          15: 'TARGET_IMPRESSION_SHARE'
        };

        const strategyName = typeof strategy === 'number' ? strategyMap[strategy] : strategy;
        const portfolioTypeName = typeof portfolioType === 'number' ? strategyMap[portfolioType] : portfolioType;

        // Vérifier si c'est un filtre portefeuille (se termine par _PORTFOLIO)
        if (settings.biddingStrategy.endsWith('_PORTFOLIO')) {
          const baseStrategy = settings.biddingStrategy.replace('_PORTFOLIO', '');
          return isPortfolio && portfolioTypeName === baseStrategy;
        }

        // Filtre standard (non-portefeuille)
        return !isPortfolio && strategyName === settings.biddingStrategy;
      });
    }

    // Filtre par recommandation (basé sur la couleur effective, pas la valeur brute)
    if (recommendationFilter) {
      if (recommendationFilter === 'increase' || recommendationFilter === 'decrease') {
        result = result.filter(item => getEffectiveRecommendationColor(item) === recommendationFilter);
      } else {
        result = result.filter(item => item.recommendation === recommendationFilter);
      }
    }

    // Filtre par priorité
    if (priorityFilter) {
      result = result.filter(item => item.priority === priorityFilter);
    }

    // Filtre par recherche
    if (searchTerm && searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(item => {
        const name = (item.name || item.campaignName || '').toLowerCase();
        const adgroupName = (item.adgroupName || '').toLowerCase();
        return name.includes(search) || adgroupName.includes(search);
      });
    }

    // Tri
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    const recommendationOrder = { increase: 0, test: 1, budget: 2, maintain: 3, decrease: 4 };

    result.sort((a, b) => {
      let valA, valB;

      switch (sortColumn) {
        case 'name':
          valA = (a.name || a.campaignName || '').toLowerCase();
          valB = (b.name || b.campaignName || '').toLowerCase();
          return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'priority':
          valA = priorityOrder[a.priority] ?? 3;
          valB = priorityOrder[b.priority] ?? 3;
          break;
        case 'recommendation':
          valA = recommendationOrder[a.recommendation] ?? 5;
          valB = recommendationOrder[b.recommendation] ?? 5;
          break;
        case 'impressions':
          valA = a.impressions || 0;
          valB = b.impressions || 0;
          break;
        case 'clicks':
          valA = a.clicks || 0;
          valB = b.clicks || 0;
          break;
        case 'cost':
          valA = a.cost || 0;
          valB = b.cost || 0;
          break;
        case 'roas':
          valA = a.roas || 0;
          valB = b.roas || 0;
          break;
        case 'cos':
          valA = a.conversionsValue > 0 ? (a.cost / a.conversionsValue) * 100 : Infinity;
          valB = b.conversionsValue > 0 ? (b.cost / b.conversionsValue) * 100 : Infinity;
          break;
        case 'cpa':
          valA = a.cpa || Infinity;
          valB = b.cpa || Infinity;
          break;
        case 'cpc':
          // Calculer CPC depuis cost/clicks si non défini
          valA = a.cpc || a.averageCpc || (a.clicks > 0 ? a.cost / a.clicks : -1);
          valB = b.cpc || b.averageCpc || (b.clicks > 0 ? b.cost / b.clicks : -1);
          // Mettre les valeurs négatives (pas de CPC) à la fin
          if (valA < 0) valA = sortDirection === 'asc' ? Infinity : -Infinity;
          if (valB < 0) valB = sortDirection === 'asc' ? Infinity : -Infinity;
          break;
        case 'conversionRate':
          valA = a.conversionRate || (a.clicks > 0 ? (a.conversions / a.clicks) * 100 : -1);
          valB = b.conversionRate || (b.clicks > 0 ? (b.conversions / b.clicks) * 100 : -1);
          if (valA < 0) valA = sortDirection === 'asc' ? Infinity : -Infinity;
          if (valB < 0) valB = sortDirection === 'asc' ? Infinity : -Infinity;
          break;
        case 'impressionShare':
          valA = a.impressionShare || -1;
          valB = b.impressionShare || -1;
          if (valA < 0) valA = sortDirection === 'asc' ? Infinity : -Infinity;
          if (valB < 0) valB = sortDirection === 'asc' ? Infinity : -Infinity;
          break;
        case 'lostISRank':
          valA = a.lostISRank || 0;
          valB = b.lostISRank || 0;
          break;
        case 'conversions':
          valA = a.conversions || 0;
          valB = b.conversions || 0;
          break;
        case 'conversionsValue':
          valA = a.conversionsValue || 0;
          valB = b.conversionsValue || 0;
          break;
        case 'daysSinceLastChange':
          // Mettre les "null" (aucune modif) à la fin
          valA = a.daysSinceLastChange !== null ? a.daysSinceLastChange : 9999;
          valB = b.daysSinceLastChange !== null ? b.daysSinceLastChange : 9999;
          break;
        default:
          valA = priorityOrder[a.priority] ?? 3;
          valB = priorityOrder[b.priority] ?? 3;
      }

      return sortDirection === 'asc' ? valA - valB : valB - valA;
    });

    return result;
  }, [data, flattenedAdgroups, recommendationFilter, priorityFilter, sortColumn, sortDirection, settings.biddingStrategy, settings.campaignType, settings.granularity, searchTerm]);

  // Filtrer et trier les données hiérarchiques
  const filteredHierarchicalData = useMemo(() => {
    let result = [...hierarchicalData];

    // Filtre par type de campagne
    const effectiveCampaignType = settings.campaignType;
    if (effectiveCampaignType && effectiveCampaignType !== 'ALL') {
      result = result.filter(campaign => {
        const itemType = campaign.campaignType;
        const typeMap = { 2: 'SEARCH', 3: 'DISPLAY', 4: 'SHOPPING', 5: 'HOTEL', 6: 'VIDEO', 7: 'MULTI_CHANNEL', 8: 'LOCAL', 9: 'SMART', 10: 'PERFORMANCE_MAX', 11: 'LOCAL_SERVICES', 12: 'DISCOVERY', 13: 'TRAVEL', 14: 'DEMAND_GEN' };
        const typeName = typeof itemType === 'number' ? typeMap[itemType] : itemType;
        return typeName === effectiveCampaignType;
      });
    }

    // Filtre par stratégie d'enchères
    if (settings.biddingStrategy && settings.biddingStrategy !== 'ALL') {
      result = result.filter(campaign => {
        const strategy = campaign.biddingStrategy;
        const portfolioType = campaign.portfolioStrategy?.type;
        const isPortfolio = campaign.isPortfolioStrategy === true;

        const strategyMap = {
          2: 'ENHANCED_CPC', 3: 'MANUAL_CPC', 6: 'TARGET_CPA', 8: 'TARGET_ROAS',
          9: 'TARGET_SPEND', 10: 'MAXIMIZE_CONVERSIONS', 11: 'MAXIMIZE_CONVERSION_VALUE', 15: 'TARGET_IMPRESSION_SHARE'
        };

        const strategyName = typeof strategy === 'number' ? strategyMap[strategy] : strategy;
        const portfolioTypeName = typeof portfolioType === 'number' ? strategyMap[portfolioType] : portfolioType;

        if (settings.biddingStrategy.endsWith('_PORTFOLIO')) {
          const baseStrategy = settings.biddingStrategy.replace('_PORTFOLIO', '');
          return isPortfolio && portfolioTypeName === baseStrategy;
        }
        return !isPortfolio && strategyName === settings.biddingStrategy;
      });
    }

    // Filtre par type de recommandation (couleur effective)
    if (recommendationFilter) {
      result = result.filter(campaign => {
        const effectiveColor = getEffectiveRecommendationColor(campaign);
        return effectiveColor === recommendationFilter;
      });
    }

    // Filtre par priorité
    if (priorityFilter) {
      result = result.filter(campaign => campaign.priority === priorityFilter);
    }

    // Filtre par recherche
    if (searchTerm && searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(campaign => {
        const name = (campaign.campaignName || '').toLowerCase();
        return name.includes(search);
      });
    }

    // Tri
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    const recommendationOrder = { increase: 0, test: 1, budget: 2, maintain: 3, decrease: 4 };
    result.sort((a, b) => {
      let valA, valB;
      switch (sortColumn) {
        case 'name':
          valA = (a.campaignName || '').toLowerCase();
          valB = (b.campaignName || '').toLowerCase();
          return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'clicks':
          valA = a.clicks || 0;
          valB = b.clicks || 0;
          break;
        case 'cost':
          valA = a.cost || 0;
          valB = b.cost || 0;
          break;
        case 'roas':
          valA = a.roas || 0;
          valB = b.roas || 0;
          break;
        case 'cos':
          valA = a.conversionsValue > 0 ? (a.cost / a.conversionsValue) * 100 : Infinity;
          valB = b.conversionsValue > 0 ? (b.cost / b.conversionsValue) * 100 : Infinity;
          break;
        case 'cpa':
          valA = a.cpa || Infinity;
          valB = b.cpa || Infinity;
          break;
        case 'cpc':
          // Calculer CPC depuis cost/clicks si non défini
          valA = a.cpc || a.averageCpc || (a.clicks > 0 ? a.cost / a.clicks : -1);
          valB = b.cpc || b.averageCpc || (b.clicks > 0 ? b.cost / b.clicks : -1);
          if (valA < 0) valA = sortDirection === 'asc' ? Infinity : -Infinity;
          if (valB < 0) valB = sortDirection === 'asc' ? Infinity : -Infinity;
          break;
        case 'conversionRate':
          valA = a.conversionRate || (a.clicks > 0 ? (a.conversions / a.clicks) * 100 : -1);
          valB = b.conversionRate || (b.clicks > 0 ? (b.conversions / b.clicks) * 100 : -1);
          if (valA < 0) valA = sortDirection === 'asc' ? Infinity : -Infinity;
          if (valB < 0) valB = sortDirection === 'asc' ? Infinity : -Infinity;
          break;
        case 'conversions':
          valA = a.conversions || 0;
          valB = b.conversions || 0;
          break;
        case 'impressionShare':
          valA = a.impressionShare || -1;
          valB = b.impressionShare || -1;
          if (valA < 0) valA = sortDirection === 'asc' ? Infinity : -Infinity;
          if (valB < 0) valB = sortDirection === 'asc' ? Infinity : -Infinity;
          break;
        case 'daysSinceLastChange':
          valA = a.daysSinceLastChange !== null ? a.daysSinceLastChange : 9999;
          valB = b.daysSinceLastChange !== null ? b.daysSinceLastChange : 9999;
          break;
        case 'recommendation':
          valA = recommendationOrder[a.recommendation] ?? 5;
          valB = recommendationOrder[b.recommendation] ?? 5;
          break;
        default:
          valA = priorityOrder[a.priority] ?? 3;
          valB = priorityOrder[b.priority] ?? 3;
      }
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    });

    return result;
  }, [hierarchicalData, recommendationFilter, priorityFilter, sortColumn, sortDirection, settings.biddingStrategy, settings.campaignType, settings.granularity, searchTerm]);

  // Stats recalculées avec les couleurs effectives (pour cohérence avec le filtrage)
  // Note: budget est inclus dans increase via getEffectiveRecommendationColor
  const effectiveStats = useMemo(() => {
    return {
      total: data.length,
      increase: data.filter(item => getEffectiveRecommendationColor(item) === 'increase').length,
      decrease: data.filter(item => getEffectiveRecommendationColor(item) === 'decrease').length
    };
  }, [data]);

  // Options de filtres dynamiques basées sur les données réelles
  const availableCampaignTypes = useMemo(() => {
    const typeMap = {
      2: 'SEARCH',
      3: 'DISPLAY',
      4: 'SHOPPING',
      5: 'HOTEL',
      6: 'VIDEO',
      7: 'MULTI_CHANNEL',
      8: 'LOCAL',
      9: 'SMART',
      10: 'PERFORMANCE_MAX',
      11: 'LOCAL_SERVICES',
      12: 'DISCOVERY',
      13: 'TRAVEL',
      14: 'DEMAND_GEN'
    };
    const typeLabels = {
      'SEARCH': 'Search',
      'DISPLAY': 'Display',
      'SHOPPING': 'Shopping',
      'HOTEL': 'Hotel',
      'VIDEO': 'Video',
      'MULTI_CHANNEL': 'Multi-channel',
      'LOCAL': 'Local',
      'SMART': 'Smart',
      'PERFORMANCE_MAX': 'Performance Max',
      'LOCAL_SERVICES': 'Local Services',
      'DISCOVERY': 'Discovery',
      'DEMAND_GEN': 'Demand Gen',
      'TRAVEL': 'Travel'
    };
    const types = new Set();
    data.forEach(item => {
      const itemType = item.type || item.campaignType;
      const typeName = typeof itemType === 'number' ? typeMap[itemType] : itemType;
      if (typeName && typeLabels[typeName]) {
        types.add(typeName);
      }
    });
    return Array.from(types).map(type => ({
      value: type,
      label: typeLabels[type] || type
    }));
  }, [data]);

  const availableBiddingStrategies = useMemo(() => {
    const strategyMap = {
      2: 'ENHANCED_CPC',
      3: 'MANUAL_CPC',
      6: 'TARGET_CPA',
      8: 'TARGET_ROAS',
      9: 'TARGET_SPEND',
      10: 'MAXIMIZE_CONVERSIONS',
      11: 'MAXIMIZE_CONVERSION_VALUE',
      15: 'TARGET_IMPRESSION_SHARE'
    };
    const strategyLabels = {
      'TARGET_ROAS': 'ROAS cible',
      'TARGET_CPA': 'CPA cible',
      'MAXIMIZE_CONVERSION_VALUE': 'Max. valeur conv.',
      'MAXIMIZE_CONVERSIONS': 'Max. conversions',
      'TARGET_IMPRESSION_SHARE': 'Taux d\'impression',
      'MANUAL_CPC': 'CPC manuel',
      'ENHANCED_CPC': 'CPC optimisé',
      'TARGET_SPEND': 'Maximiser clics'
    };
    const strategies = new Map(); // { strategyName: { hasStandard: bool, hasPortfolio: bool } }

    data.forEach(item => {
      const strategy = item.biddingStrategy;
      const isPortfolio = item.isPortfolioStrategy === true;
      const portfolioType = item.portfolioStrategy?.type;

      // Déterminer le nom de la stratégie
      let strategyName;
      if (isPortfolio && portfolioType) {
        strategyName = typeof portfolioType === 'number' ? strategyMap[portfolioType] : portfolioType;
      } else {
        strategyName = typeof strategy === 'number' ? strategyMap[strategy] : strategy;
      }

      if (strategyName && strategyLabels[strategyName]) {
        if (!strategies.has(strategyName)) {
          strategies.set(strategyName, { hasStandard: false, hasPortfolio: false });
        }
        const entry = strategies.get(strategyName);
        if (isPortfolio) {
          entry.hasPortfolio = true;
        } else {
          entry.hasStandard = true;
        }
      }
    });

    const result = [];
    strategies.forEach((entry, strategyName) => {
      if (entry.hasStandard) {
        result.push({
          value: strategyName,
          label: strategyLabels[strategyName]
        });
      }
      if (entry.hasPortfolio) {
        result.push({
          value: `${strategyName}_PORTFOLIO`,
          label: `${strategyLabels[strategyName]} (portefeuille)`
        });
      }
    });

    // Trier par ordre alphabétique sur le label
    return result.sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  }, [data]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num || 0);
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return '-';
    return `${num.toFixed(1)}%`;
  };

  // Formater la date du dernier changement
  const formatLastChangeDate = (dateString, daysSince) => {
    if (!dateString) return null;

    if (daysSince === 0) return "Aujourd'hui";
    if (daysSince === 1) return "Hier";
    if (daysSince <= 7) return `Il y a ${daysSince} jours`;
    if (daysSince <= 14) return `Il y a ${daysSince} jours`;
    if (daysSince <= 30) return `Il y a ${Math.floor(daysSince / 7)} sem.`;

    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Formater le nombre de jours depuis la dernière modification
  const formatDaysAgo = (daysSince) => {
    if (daysSince === null || daysSince === undefined) return null;
    if (daysSince === 0) return "Aujourd'hui";
    if (daysSince === 1) return "Il y a 1 jour";
    return `Il y a ${daysSince} jours`;
  };

  // Badge de cooldown (période d'attente recommandée) - cliquable si détails disponibles
  const getCooldownBadge = (item) => {
    const daysSince = item.daysSinceLastChange;
    const hasDetails = item.lastBidChangeDate !== null;
    const dateDisplay = formatDaysAgo(daysSince);

    const handleClick = (e) => {
      e.stopPropagation(); // Empêcher l'ouverture du modal d'évolution
      if (hasDetails) {
        setSelectedChangeDetails(item);
      }
    };

    const cursorStyle = hasDetails ? 'pointer' : 'default';

    if (daysSince === null) {
      return (
        <span style={{
          fontSize: '12px',
          color: '#9ca3af'
        }}>
          —
        </span>
      );
    }

    // Style commun pour le lien cliquable avec soulignement au survol
    const linkStyle = {
      fontSize: '12px',
      color: '#374151',
      fontWeight: '600',
      textDecoration: 'none',
      cursor: hasDetails ? 'pointer' : 'default'
    };

    const handleMouseEnter = (e) => {
      if (hasDetails) {
        e.target.style.textDecoration = 'underline';
      }
    };

    const handleMouseLeave = (e) => {
      e.target.style.textDecoration = 'none';
    };

    // Cooldown en cours (< 7 jours) - Afficher seulement la date sans message d'attente
    if (daysSince < 7) {
      return (
        <div
          onClick={handleClick}
          style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
          title={hasDetails ? "Cliquer pour voir les détails" : ""}
        >
          <span
            style={linkStyle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {dateDisplay}
          </span>
        </div>
      );
    }

    // Prudence recommandée (7-14 jours) - Afficher seulement la date sans badge
    if (daysSince <= 14) {
      return (
        <div
          onClick={handleClick}
          style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
          title={hasDetails ? "Cliquer pour voir les détails" : ""}
        >
          <span
            style={linkStyle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {dateDisplay}
          </span>
        </div>
      );
    }

    // > 14 jours - Afficher seulement la date
    return (
      <div
        onClick={handleClick}
        style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
        title={hasDetails ? "Cliquer pour voir les détails" : ""}
      >
        <span
          style={linkStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {dateDisplay}
        </span>
      </div>
    );
  };

  // Badge de recommandation
  // parentCampaign: optionnel, utilisé pour les adgroups afin d'hériter du type de stratégie de la campagne parente
  const getRecommendationBadge = (recommendation, adjustmentRange, item = null, parentCampaign = null) => {
    // Déterminer le type de stratégie pour adapter le label
    const getStrategyLabel = () => {
      if (!item) return null;

      // Pour les adgroups, utiliser la stratégie de la campagne parente
      const isAdgroup = item.level === 'adgroup';
      const strategySource = (isAdgroup && parentCampaign) ? parentCampaign : item;

      const biddingStr = typeof strategySource.biddingStrategy === 'string' ? strategySource.biddingStrategy : '';
      const biddingLabel = typeof strategySource.biddingStrategyLabel === 'string' ? strategySource.biddingStrategyLabel : '';

      // Convertir biddingStrategy en nombre pour comparaison (gère "6" vs 6)
      const biddingNum = parseInt(strategySource.biddingStrategy, 10);

      // IMPORTANT: Pour les adgroups, on ne peut PAS modifier le CPC max (uniquement niveau campagne/portfolio)
      // On utilise donc CPA/ROAS/CPC selon la stratégie de la campagne parente

      // Vérifier si c'est une stratégie de portefeuille avec CPC max défini
      // SAUF pour les adgroups où on ne peut pas modifier le CPC max
      if (!isAdgroup && strategySource.isPortfolioStrategy && strategySource.portfolioStrategy?.maxBidLimit) {
        return 'CPC_MAX';
      }

      // Vérifier si c'est une stratégie Taux d'impression cible avec CPC max
      // SAUF pour les adgroups
      if (!isAdgroup && (biddingNum === 15 || biddingStr === 'TARGET_IMPRESSION_SHARE') && strategySource.targetImpressionShareMaxCpc) {
        return 'CPC_MAX';
      }

      // Vérifier si c'est une stratégie ROAS avec targetRoas défini
      // Types numériques: 8 = TARGET_ROAS, 11 = MAXIMIZE_CONVERSION_VALUE
      const portfolioType = strategySource.portfolioStrategy?.type;
      const portfolioTypeNum = parseInt(portfolioType, 10);

      if (strategySource.targetRoas ||
          biddingNum === 8 ||
          portfolioTypeNum === 8 ||
          portfolioType === 'TARGET_ROAS' ||
          biddingStr.includes('ROAS') ||
          biddingStr.includes('TARGET_ROAS') ||
          biddingLabel.includes('ROAS') ||
          biddingLabel.toLowerCase().includes('roas cible')) {
        return 'ROAS';
      }

      // MAXIMIZE_CONVERSION_VALUE (11) sans targetRoas = recommander de définir ROAS cible
      if (biddingNum === 11 || portfolioTypeNum === 11 ||
          portfolioType === 'MAXIMIZE_CONVERSION_VALUE' ||
          biddingStr.includes('MAXIMIZE_CONVERSION_VALUE') ||
          biddingLabel.toLowerCase().includes('maximiser la valeur')) {
        // Sans targetRoas défini, recommander de définir un ROAS cible
        if (!strategySource.targetRoas) {
          return 'AUTO_VALUE';
        }
        return 'ROAS';
      }

      // Vérifier si c'est une stratégie CPA avec targetCpa défini
      // Types numériques: 2 = TARGET_CPA
      if (strategySource.targetCpa ||
          biddingNum === 2 ||
          portfolioTypeNum === 2 ||
          portfolioType === 'TARGET_CPA' ||
          biddingStr.includes('CPA') ||
          biddingStr.includes('TARGET_CPA') ||
          biddingLabel.includes('CPA') ||
          biddingLabel.toLowerCase().includes('cpa cible')) {
        return 'CPA';
      }

      // MAXIMIZE_CONVERSIONS (10) sans targetCpa = recommander de définir CPA cible
      // Note: le code 6 est TARGET_CPA, 10 est MAXIMIZE_CONVERSIONS
      if (biddingNum === 10 || portfolioTypeNum === 10 ||
          portfolioType === 'MAXIMIZE_CONVERSIONS' ||
          biddingStr.includes('MAXIMIZE_CONVERSIONS') ||
          biddingLabel.toLowerCase().includes('maximiser les conversions')) {
        // Sans targetCpa défini (ni au niveau campagne, ni au niveau portfolio), recommander de définir un CPA cible
        // Pour les portfolios CPA, le targetValue contient le CPA cible
        const hasCpaTarget = strategySource.targetCpa ||
          (portfolioTypeNum === 10 && strategySource.portfolioStrategy?.targetValue) ||
          (portfolioTypeNum === 2 && strategySource.portfolioStrategy?.targetValue);
        if (!hasCpaTarget) {
          return 'AUTO_CONVERSIONS';
        }
        return 'CPA';
      }

      // TARGET_SPEND (9) = Maximiser les clics
      // Si pas de limite CPC max définie, recommander d'en définir une
      if (biddingNum === 9 || biddingNum === 12 || portfolioTypeNum === 9 || portfolioTypeNum === 12 ||
          portfolioType === 'TARGET_SPEND' ||
          biddingStr.includes('TARGET_SPEND') ||
          biddingLabel.toLowerCase().includes('maximiser les clics') ||
          biddingLabel.toLowerCase().includes('maximiser clics')) {
        // Vérifier si une limite CPC max est définie (via portfolio ou campagne)
        const hasMaxCpc = strategySource.portfolioStrategy?.maxBidLimit || strategySource.targetSpendMaxCpc;
        if (!hasMaxCpc) {
          return 'AUTO_CLICKS';
        }
        return 'CPC_MAX';
      }

      // TARGET_IMPRESSION_SHARE (15) = Taux d'impressions cible
      // Si pas de limite CPC max définie, recommander d'en définir une
      if (biddingNum === 15 || portfolioTypeNum === 15 ||
          portfolioType === 'TARGET_IMPRESSION_SHARE' ||
          biddingStr.includes('TARGET_IMPRESSION_SHARE') ||
          biddingLabel.toLowerCase().includes('taux d\'impression')) {
        // Vérifier si une limite CPC max est définie
        const hasMaxCpc = strategySource.targetImpressionShareMaxCpc;
        if (!hasMaxCpc) {
          return 'AUTO_IMPRESSION_SHARE';
        }
        return 'CPC_MAX';
      }

      // Vérifier si c'est une stratégie CPC (manuel ou optimisé)
      // 3 = MANUAL_CPC, 4 = ENHANCED_CPC
      if (biddingNum === 3 ||
          biddingNum === 4 ||
          biddingStr.includes('CPC') ||
          biddingStr.includes('MANUAL_CPC') ||
          biddingStr.includes('ENHANCED_CPC') ||
          biddingLabel.includes('CPC manuel') ||
          biddingLabel.includes('CPC optimisé')) {
        return 'CPC';
      }

      return null;
    };

    const strategyType = getStrategyLabel();

    // Labels adaptés selon la stratégie
    const getLabel = (baseReco) => {
      // Pour les stratégies automatiques sans contrainte CPC, recommander de définir une limite
      if (strategyType === 'AUTO_CLICKS') {
        return 'Définir CPC max';
      }
      if (strategyType === 'AUTO_IMPRESSION_SHARE') {
        return 'Définir CPC max';
      }
      if (baseReco === 'increase') {
        if (strategyType === 'CPC_MAX') return 'Baisser CPC max';
        if (strategyType === 'ROAS' || strategyType === 'AUTO_VALUE') return 'Augmenter ROAS';
        if (strategyType === 'CPA' || strategyType === 'AUTO_CONVERSIONS') return 'Augmenter CPA';
        return 'Augmenter CPC';
      }
      if (baseReco === 'decrease') {
        if (strategyType === 'CPC_MAX') return 'Augmenter CPC max';
        if (strategyType === 'ROAS' || strategyType === 'AUTO_VALUE') return 'Baisser ROAS';
        if (strategyType === 'CPA' || strategyType === 'AUTO_CONVERSIONS') return 'Baisser CPA';
        return 'Baisser CPC';
      }
      return null;
    };

    // Calculer les valeurs actuelles et recommandées
    const getValuesDisplay = () => {
      if (!item || recommendation === 'test') {
        return null;
      }

      // Pour les stratégies AUTO_*, afficher la valeur actuelle et recommandée
      // adjustmentRange vient du backend et contient [min%, max%] selon l'agressivité choisie
      const adjustPercent = adjustmentRange?.[1] || 15; // Par défaut 15%

      if (strategyType === 'AUTO_CONVERSIONS') {
        const currentCpa = item.cpa || (item.conversions > 0 ? item.cost / item.conversions : null);
        if (currentCpa) {
          // Si maintain, pas de changement
          if (recommendation === 'maintain') {
            return `${currentCpa.toFixed(2)}€ → ${currentCpa.toFixed(2)}€`;
          }
          const recommendedCpa = currentCpa * (1 - adjustPercent / 100);
          return `${currentCpa.toFixed(2)}€ → ${recommendedCpa.toFixed(2)}€`;
        }
        return null;
      }
      if (strategyType === 'AUTO_VALUE') {
        const currentRoas = item.roas || (item.cost > 0 ? item.conversionsValue / item.cost : null);
        if (currentRoas) {
          // Si maintain, pas de changement
          if (recommendation === 'maintain') {
            return `${(currentRoas * 100).toFixed(0)}% → ${(currentRoas * 100).toFixed(0)}%`;
          }
          const recommendedRoas = currentRoas * (1 + adjustPercent / 100);
          return `${(currentRoas * 100).toFixed(0)}% → ${(recommendedRoas * 100).toFixed(0)}%`;
        }
        return null;
      }
      if (strategyType === 'AUTO_CLICKS' || strategyType === 'AUTO_IMPRESSION_SHARE') {
        const currentCpc = item.cpc || item.averageCpc || (item.clicks > 0 ? item.cost / item.clicks : null);
        if (currentCpc) {
          // Si maintain, pas de changement
          if (recommendation === 'maintain') {
            return `${currentCpc.toFixed(2)}€ → ${currentCpc.toFixed(2)}€`;
          }
          const recommendedCpc = currentCpc * (1 - adjustPercent / 100);
          return `${currentCpc.toFixed(2)}€ → ${recommendedCpc.toFixed(2)}€`;
        }
        return null;
      }

      let currentValue = null;
      let recommendedValue = null;
      let formatFn = (v) => `${v.toFixed(2)}€`;

      // Cas spécial pour le budget
      if (recommendation === 'budget') {
        currentValue = item.budgetAmount || null;
        if (currentValue) {
          // Recommandation d'augmenter le budget de 20-30% par défaut
          const budgetIncreaseFactor = 1.25; // +25%
          recommendedValue = currentValue * budgetIncreaseFactor;
          formatFn = (v) => `${v.toFixed(0)}€/j`;
        }
        if (currentValue && recommendedValue) {
          return `${formatFn(currentValue)} → ${formatFn(recommendedValue)}`;
        }
        return null;
      }

      // Pour 'maintain', pas d'adjustmentRange mais on permet quand même l'édition
      if (!adjustmentRange && recommendation !== 'maintain' && recommendation !== 'increase_max_cpc') {
        return null;
      }

      // Cas spécial pour increase_max_cpc (stratégie limitée par le CPC max)
      if (recommendation === 'increase_max_cpc') {
        currentValue = item.portfolioStrategy?.maxBidLimit || item.targetImpressionShareMaxCpc || null;
        if (currentValue && adjustmentRange) {
          // Augmenter le CPC max de adjustmentRange[1]%
          recommendedValue = currentValue * (1 + (adjustmentRange[1] / 100));
        }
        if (currentValue && recommendedValue) {
          return `${currentValue.toFixed(2)}€ → ${recommendedValue.toFixed(2)}€`;
        }
        return null;
      }

      if (strategyType === 'CPA') {
        // Pour les adgroups: utiliser le CPA de l'adgroup s'il existe, sinon celui de la campagne
        const isAdgroup = item.level === 'adgroup';
        if (isAdgroup) {
          // Priorité: CPA adgroup > CPA campagne > CPA portfolio
          currentValue = item.targetCpa || (parentCampaign?.targetCpa) || (parentCampaign?.portfolioStrategy?.targetValue) || null;
        } else {
          currentValue = item.targetCpa || item.portfolioStrategy?.targetValue || null;
        }
        if (currentValue) {
          if (recommendation === 'maintain') {
            recommendedValue = currentValue; // Pas de changement
          } else if (adjustmentRange) {
            const adjustFactor = recommendation === 'increase'
              ? 1 + (adjustmentRange[1] / 100)
              : 1 - (adjustmentRange[1] / 100);
            recommendedValue = currentValue * adjustFactor;
          }
        }
      } else if (strategyType === 'ROAS') {
        // Pour les adgroups: utiliser le ROAS de l'adgroup s'il existe, sinon celui de la campagne
        const isAdgroup = item.level === 'adgroup';
        if (isAdgroup) {
          // Priorité: ROAS adgroup > ROAS campagne > ROAS portfolio
          currentValue = item.targetRoas || (parentCampaign?.targetRoas) || (parentCampaign?.portfolioStrategy?.targetValue) || null;
        } else {
          currentValue = item.targetRoas || item.portfolioStrategy?.targetValue || null;
        }
        if (currentValue) {
          if (recommendation === 'maintain') {
            recommendedValue = currentValue; // Pas de changement
          } else if (adjustmentRange) {
            const adjustFactor = recommendation === 'decrease'
              ? 1 - (adjustmentRange[1] / 100)
              : 1 + (adjustmentRange[1] / 100);
            recommendedValue = currentValue * adjustFactor;
          }
        }
        formatFn = (v) => `${(v * 100).toFixed(0)}%`;
      } else if (strategyType === 'CPC_MAX') {
        // CPC max peut venir d'une stratégie de portefeuille ou de Target Impression Share
        currentValue = item.portfolioStrategy?.maxBidLimit || item.targetImpressionShareMaxCpc || null;
        if (currentValue) {
          if (recommendation === 'maintain') {
            recommendedValue = currentValue; // Pas de changement
          } else if (adjustmentRange) {
            // Pour CPC_MAX: 'increase' = "Baisser CPC max" → diminuer la valeur
            //               'decrease' = "Augmenter CPC max" → augmenter la valeur
            const adjustFactor = recommendation === 'increase'
              ? 1 - (adjustmentRange[1] / 100)
              : 1 + (adjustmentRange[1] / 100);
            recommendedValue = currentValue * adjustFactor;
          }
        }
      } else if (strategyType === 'CPC') {
        // Pour CPC manuel, utiliser currentBid en priorité, sinon cpcBid (groupes de produits), sinon les métriques de performance
        currentValue = item.currentBid || item.cpcBid || item.averageCpc || item.cpc || (item.clicks > 0 ? item.cost / item.clicks : null);
        if (currentValue) {
          if (recommendation === 'maintain') {
            recommendedValue = currentValue; // Pas de changement
          } else if (adjustmentRange) {
            const adjustFactor = recommendation === 'increase'
              ? 1 + (adjustmentRange[1] / 100)
              : 1 - (adjustmentRange[1] / 100);
            recommendedValue = currentValue * adjustFactor;
          }
        }
      }

      // Si toujours pas de valeur et recommendation 'maintain', essayer d'autres champs
      if (!currentValue && recommendation === 'maintain') {
        currentValue = item.currentBid || item.averageCpc || item.targetCpa || item.targetRoas || null;
        if (currentValue) {
          recommendedValue = currentValue;
          if (item.targetRoas) {
            formatFn = (v) => `${(v * 100).toFixed(0)}%`;
          }
        }
      }

      if (currentValue && recommendedValue) {
        return `${formatFn(currentValue)} → ${formatFn(recommendedValue)}`;
      }
      return null;
    };

    const styles = {
      increase: { bg: '#dcfce7', color: '#166534', icon: TrendingUp },
      decrease: { bg: '#fee2e2', color: '#991b1b', icon: TrendingDown },
      maintain: { bg: '#f3f4f6', color: '#374151', icon: ArrowRight },
      test: { bg: '#fef3c7', color: '#92400e', icon: Target },
      budget: { bg: '#dcfce7', color: '#166534', icon: TrendingUp }, // Vert comme "increase"
      increase_max_cpc: { bg: '#dcfce7', color: '#166534', icon: TrendingUp },
      auto_action: { bg: '#fef3c7', color: '#92400e', icon: AlertTriangle } // Orange comme "action requise"
    };

    const labels = {
      increase: getLabel('increase') || 'Augmenter CPC',
      decrease: getLabel('decrease') || 'Baisser CPC',
      maintain: 'Maintenir',
      // 'test' = aucune enchère à bouger ici : données insuffisantes, ou cible impossible à
      // recalibrer faute de conversion (voir lib/verdictSansConversion côté backend).
      // « Tester » laissait croire à une expérimentation à lancer ; c'est le Diagnostic qui dit
      // quoi regarder.
      test: 'À surveiller',
      budget: 'Augmenter le budget',
      increase_max_cpc: 'Augmenter CPC max'
    };

    // Déterminer si c'est une stratégie AUTO_* (sans contrainte définie)
    const isAutoStrategy = ['AUTO_CLICKS', 'AUTO_CONVERSIONS', 'AUTO_VALUE', 'AUTO_IMPRESSION_SHARE'].includes(strategyType);

    // Inverser les couleurs pour CPC_MAX, ROAS et CPA (mais garder l'icône originale)
    // Pour AUTO_*, utiliser le style orange "action requise" seulement si performances mauvaises (decrease)
    let colorKey = recommendation;
    let iconKey = recommendation; // Garder l'icône correspondant à la recommandation originale
    if (isAutoStrategy) {
      // Afficher en orange seulement si performances mauvaises (decrease)
      // Si performances bonnes ou neutres (increase/maintain), afficher en gris "Maintenir"
      colorKey = recommendation === 'decrease' ? 'auto_action' : 'maintain';
      iconKey = colorKey;
    } else if (strategyType === 'CPC_MAX' || strategyType === 'ROAS' || strategyType === 'CPA') {
      // Pour CPA/ROAS/CPC_MAX: 'increase' = mauvaises performances (rouge), 'decrease' = bonnes performances (vert)
      // Mais on garde l'icône originale (TrendingUp pour increase, TrendingDown pour decrease)
      if (recommendation === 'increase') colorKey = 'decrease';
      else if (recommendation === 'decrease') colorKey = 'increase';
      // iconKey reste = recommendation (non inversé)
    }

    const style = styles[colorKey] || styles.maintain;
    const IconFromOriginal = styles[iconKey]?.icon || style.icon;

    // Pour AUTO_*, afficher la recommandation spécifique seulement si performances mauvaises
    let label;
    if (isAutoStrategy) {
      // Si performances mauvaises (decrease) → recommander de définir une contrainte
      // Sinon → afficher "Maintenir"
      label = recommendation === 'decrease' ? getLabel(recommendation) : 'Maintenir';
    } else {
      label = labels[recommendation] || 'Maintenir';
    }
    const Icon = IconFromOriginal;

    // Calculer les valeurs pour l'affichage avec input éditable
    const itemId = item?.campaignId || item?.id;
    const customValue = itemId ? getCustomRecommendationValue(itemId) : null;

    // Obtenir la valeur actuelle et recommandée
    const getValuesForEditing = () => {
      if (!item || recommendation === 'test') {
        return { currentValue: null, recommendedValue: null, unit: '', formatFn: (v) => v };
      }

      // Pour les stratégies AUTO_*, calculer la valeur recommandée selon l'agressivité
      // adjustmentRange vient du backend et contient [min%, max%] selon l'agressivité choisie
      const adjustPercent = adjustmentRange?.[1] || 15; // Par défaut 15%

      if (strategyType === 'AUTO_CONVERSIONS') {
        // CPA cible doit être INFÉRIEUR au CPA constaté pour améliorer les performances
        const currentCpa = item.cpa || (item.conversions > 0 ? item.cost / item.conversions : null);
        const recommendedCpa = currentCpa ? currentCpa * (1 - adjustPercent / 100) : 0;
        return {
          currentValue: currentCpa,
          recommendedValue: recommendedCpa,
          unit: '€',
          formatFn: (v) => v.toFixed(2)
        };
      }
      if (strategyType === 'AUTO_VALUE') {
        // ROAS cible doit être SUPÉRIEUR au ROAS constaté pour améliorer les performances
        const currentRoas = item.roas || (item.cost > 0 ? item.conversionsValue / item.cost : null);
        const recommendedRoas = currentRoas ? currentRoas * (1 + adjustPercent / 100) : 0;
        return {
          currentValue: currentRoas,
          recommendedValue: recommendedRoas,
          unit: '%',
          formatFn: (v) => (v * 100).toFixed(0)
        };
      }
      if (strategyType === 'AUTO_CLICKS' || strategyType === 'AUTO_IMPRESSION_SHARE') {
        // CPC max doit être INFÉRIEUR au CPC constaté pour limiter les coûts
        const currentCpc = item.cpc || item.averageCpc || (item.clicks > 0 ? item.cost / item.clicks : null);
        const recommendedCpc = currentCpc ? currentCpc * (1 - adjustPercent / 100) : 0;
        return {
          currentValue: currentCpc,
          recommendedValue: recommendedCpc,
          unit: '€',
          formatFn: (v) => v.toFixed(2)
        };
      }

      let currentValue = null;
      let recommendedValue = null;
      let unit = '€';
      let formatFn = (v) => v.toFixed(2);

      // Cas spécial pour le budget
      if (recommendation === 'budget') {
        currentValue = item.budgetAmount || null;
        unit = '€/j';
        formatFn = (v) => v.toFixed(0);
        if (currentValue && currentValue > 0) {
          const budgetIncreaseFactor = 1.25;
          recommendedValue = currentValue * budgetIncreaseFactor;
        } else {
          // Si pas de budget actuel, permettre la saisie manuelle
          recommendedValue = 0;
        }
        return { currentValue, recommendedValue, unit, formatFn };
      }

      // Pour 'maintain', pas d'adjustmentRange mais on permet quand même l'édition
      if (!adjustmentRange && recommendation !== 'maintain' && recommendation !== 'increase_max_cpc') {
        return { currentValue: null, recommendedValue: null, unit: '', formatFn: (v) => v };
      }

      // Cas spécial pour increase_max_cpc (stratégie limitée par le CPC max)
      if (recommendation === 'increase_max_cpc') {
        currentValue = item.portfolioStrategy?.maxBidLimit || item.targetImpressionShareMaxCpc || null;
        if (currentValue && adjustmentRange) {
          // Augmenter le CPC max de adjustmentRange[1]%
          recommendedValue = currentValue * (1 + (adjustmentRange[1] / 100));
        }
        return { currentValue, recommendedValue, unit: '€', formatFn: (v) => v.toFixed(2) };
      }

      if (strategyType === 'CPA') {
        // Pour les adgroups: utiliser le CPA de l'adgroup s'il existe, sinon celui de la campagne
        const isAdgroup = item.level === 'adgroup';
        if (isAdgroup) {
          // Priorité: CPA adgroup > CPA campagne > CPA portfolio
          currentValue = item.targetCpa || (parentCampaign?.targetCpa) || (parentCampaign?.portfolioStrategy?.targetValue) || null;
        } else {
          currentValue = item.targetCpa || item.portfolioStrategy?.targetValue || null;
        }
        if (currentValue) {
          if (recommendation === 'maintain') {
            recommendedValue = currentValue; // Pas de changement
          } else if (adjustmentRange) {
            const adjustFactor = recommendation === 'increase'
              ? 1 + (adjustmentRange[1] / 100)
              : 1 - (adjustmentRange[1] / 100);
            recommendedValue = currentValue * adjustFactor;
          }
        }
        unit = '€';
        formatFn = (v) => v.toFixed(2);
      } else if (strategyType === 'ROAS') {
        // Pour les adgroups: utiliser le ROAS de l'adgroup s'il existe, sinon celui de la campagne
        const isAdgroup = item.level === 'adgroup';
        if (isAdgroup) {
          // Priorité: ROAS adgroup > ROAS campagne > ROAS portfolio
          currentValue = item.targetRoas || (parentCampaign?.targetRoas) || (parentCampaign?.portfolioStrategy?.targetValue) || null;
        } else {
          currentValue = item.targetRoas || item.portfolioStrategy?.targetValue || null;
        }
        if (currentValue) {
          if (recommendation === 'maintain') {
            recommendedValue = currentValue; // Pas de changement
          } else if (adjustmentRange) {
            const adjustFactor = recommendation === 'decrease'
              ? 1 - (adjustmentRange[1] / 100)
              : 1 + (adjustmentRange[1] / 100);
            recommendedValue = currentValue * adjustFactor;
          }
        }
        unit = '%';
        formatFn = (v) => (v * 100).toFixed(0);
      } else if (strategyType === 'CPC_MAX') {
        currentValue = item.portfolioStrategy?.maxBidLimit || item.targetImpressionShareMaxCpc || null;
        if (currentValue) {
          if (recommendation === 'maintain') {
            recommendedValue = currentValue; // Pas de changement
          } else if (adjustmentRange) {
            const adjustFactor = recommendation === 'increase'
              ? 1 - (adjustmentRange[1] / 100)
              : 1 + (adjustmentRange[1] / 100);
            recommendedValue = currentValue * adjustFactor;
          }
        }
        unit = '€';
        formatFn = (v) => v.toFixed(2);
      } else if (strategyType === 'CPC') {
        // Utiliser currentBid (enchère définie) en priorité, sinon les métriques de performance
        currentValue = item.currentBid || item.averageCpc || item.cpc || (item.clicks > 0 ? item.cost / item.clicks : null);
        if (currentValue) {
          if (recommendation === 'maintain') {
            recommendedValue = currentValue; // Pas de changement
          } else if (adjustmentRange) {
            const adjustFactor = recommendation === 'increase'
              ? 1 + (adjustmentRange[1] / 100)
              : 1 - (adjustmentRange[1] / 100);
            recommendedValue = currentValue * adjustFactor;
          }
        }
        unit = '€';
        formatFn = (v) => v.toFixed(2);
      }

      // Si toujours pas de valeur et recommendation 'maintain', essayer d'autres champs
      if (!currentValue && recommendation === 'maintain') {
        // Fallback pour les cas sans données de performance
        currentValue = item.currentBid || item.averageCpc || item.targetCpa || item.targetRoas || null;
        if (currentValue) {
          recommendedValue = currentValue;
          // Déterminer l'unité selon le type de valeur trouvée
          if (item.targetRoas) {
            unit = '%';
            formatFn = (v) => (v * 100).toFixed(0);
          } else {
            unit = '€';
            formatFn = (v) => v.toFixed(2);
          }
        }
      }

      // Pour 'maintain' sans valeur, permettre quand même l'édition avec une valeur par défaut
      if (!currentValue && recommendation === 'maintain' && strategyType) {
        // Déterminer l'unité par défaut selon le type de stratégie
        if (strategyType === 'CPA') {
          unit = '€';
          formatFn = (v) => v.toFixed(2);
          currentValue = 0;
          recommendedValue = 0;
        } else if (strategyType === 'ROAS') {
          unit = '%';
          formatFn = (v) => (v * 100).toFixed(0);
          currentValue = 0;
          recommendedValue = 0;
        } else if (strategyType === 'CPC' || strategyType === 'CPC_MAX') {
          unit = '€';
          formatFn = (v) => v.toFixed(2);
          currentValue = 0;
          recommendedValue = 0;
        }
      }

      return { currentValue, recommendedValue, unit, formatFn };
    };

    const { currentValue, recommendedValue, unit, formatFn } = getValuesForEditing();
    const hasValues = currentValue !== null && recommendedValue !== null;

    // Déterminer si c'est une campagne CPC manuel (pour désactiver l'édition au niveau campagne)
    // Pour les campagnes CPC manuel, la modification du CPC doit se faire au niveau groupe d'annonces
    const isAdgroup = item?.level === 'adgroup';
    const biddingNum = parseInt(item?.biddingStrategy, 10);
    const biddingStr = typeof item?.biddingStrategy === 'string' ? item?.biddingStrategy : '';
    const biddingLabel = typeof item?.biddingStrategyLabel === 'string' ? item?.biddingStrategyLabel : '';
    const isManualCpc = biddingNum === 3 || biddingStr === 'MANUAL_CPC' || biddingLabel?.includes('CPC manuel');
    // Pour les campagnes CPC manuel au niveau campagne, ne pas permettre l'édition
    const canEditManualCpc = !isManualCpc || isAdgroup;

    // Pour les campagnes Shopping avec stratégie ROAS, ne pas permettre l'édition au niveau groupe d'annonces
    // Le ROAS cible se définit uniquement au niveau campagne ou stratégie de portefeuille
    const itemType = item?.type || item?.campaignType;
    const isShopping = itemType === 'SHOPPING' || itemType === 4 || itemType === '4';
    const canEditRoasAtAdgroup = !(isAdgroup && strategyType === 'ROAS' && isShopping);

    // Pour les campagnes Shopping avec CPC manuel, ne pas permettre l'édition au niveau adgroup
    // Les enchères CPC se définissent au niveau groupe de produits
    const canEditCpcAtAdgroup = !(isAdgroup && strategyType === 'CPC' && isShopping);

    // Permettre l'édition pour 'maintain' même si la valeur est 0 (pas de données)
    // Pour AUTO_*, permettre l'édition seulement si performances mauvaises (decrease)
    // Pour 'budget', toujours permettre l'édition (même sur CPC manuel car c'est le budget, pas le CPC)
    const canEditAuto = isAutoStrategy && recommendation === 'decrease';
    const canEdit = ((hasValues || canEditAuto || (recommendation === 'maintain' && strategyType)) && canEditManualCpc && canEditRoasAtAdgroup && canEditCpcAtAdgroup)
      || recommendation === 'budget'; // Budget toujours éditable

    // hasCustomValue est true si une valeur est dans le Map (même chaîne vide pendant l'édition)
    const hasCustomValue = customRecommendationValues.has(itemId);
    // Afficher la valeur custom si elle existe, sinon la valeur recommandée formatée
    // Pour 'maintain' sans données, afficher champ vide pour permettre la saisie
    const displayRecommendedValue = hasCustomValue
      ? (customValue || '')
      : (recommendedValue !== null && recommendedValue > 0 ? formatFn(recommendedValue) : '');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '5px 12px',
            fontSize: '14px',
            fontWeight: '600',
            borderRadius: '9999px',
            backgroundColor: style.bg,
            color: style.color
          }}>
            <Icon size={16} />
            {label}
          </span>
        </div>
        {canEdit && (() => {
          // Calculer updateType et applyKey pour savoir si appliqué
          const getUpdateType = () => {
            if (recommendation === 'budget') return 'budget';
            if (strategyType === 'CPA') return 'targetCpa';
            if (strategyType === 'ROAS') return 'targetRoas';
            if (strategyType === 'CPC_MAX') return 'maxCpc';
            if (strategyType === 'CPC') return 'cpcBid';
            // Pour les stratégies AUTO_*, définir le type de mise à jour approprié
            if (strategyType === 'AUTO_CONVERSIONS') return 'targetCpa';
            if (strategyType === 'AUTO_VALUE') return 'targetRoas';
            if (strategyType === 'AUTO_CLICKS') return 'maxCpc';
            if (strategyType === 'AUTO_IMPRESSION_SHARE') return 'maxCpc';
            return null;
          };
          const updateType = getUpdateType();
          const applyKey = updateType ? `${itemId}-${updateType}` : null;
          const isApplied = applyKey && appliedRecommendations.has(applyKey);
          const appliedValue = applyKey && appliedValues.get(applyKey);

          // Si appliqué, afficher la valeur appliquée (déjà formatée, pas besoin de reformater)
          const valueToShow = isApplied && appliedValue !== undefined ? appliedValue : displayRecommendedValue;

          // Afficher "-" si pas de valeur actuelle réelle (0 = placeholder)
          const hasRealCurrentValue = currentValue && currentValue > 0;

          return (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>
                {hasRealCurrentValue ? `${formatFn(currentValue)}${unit}` : '-'}
              </span>
              <span style={{ fontSize: '14px', color: '#9ca3af' }}>→</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {isApplied ? (
                  // Afficher la valeur appliquée en lecture seule
                  <span style={{
                    padding: '4px 6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#166534',
                    backgroundColor: '#dcfce7',
                    borderRadius: '4px',
                    minWidth: '65px',
                    textAlign: 'center'
                  }}>
                    {valueToShow}
                  </span>
                ) : (
                  <EditableRecommendationInput
                    initialValue={displayRecommendedValue}
                    recommendedValue={recommendedValue}
                    formatFn={formatFn}
                    onValueChange={(val) => setCustomRecommendationValue(itemId, val)}
                    isCustom={hasCustomValue}
                  />
                )}
                <span style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>{unit}</span>
                {!isApplied && (hasCustomValue && customValue && customValue !== formatFn(recommendedValue)) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      resetCustomRecommendationValue(itemId);
                    }}
                    style={{
                      padding: '2px 4px',
                      fontSize: '10px',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: '#6b7280',
                      marginLeft: '2px'
                    }}
                    title="Réinitialiser"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
            {/* Bouton Appliquer sur nouvelle ligne */}
            {item && recommendedValue !== null && updateType && (() => {
              const isApplying = applyingRecommendations.has(applyKey);

              // Valeur à appliquer: valeur personnalisée si remplie, sinon valeur recommandée
              const valueToApply = displayRecommendedValue !== '' ? displayRecommendedValue : formatFn(recommendedValue);

              return (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    applyRecommendation(item, valueToApply, strategyType, updateType);
                  }}
                  disabled={isApplying || isApplied || !valueToApply}
                  style={{
                    padding: '6px 14px',
                    fontSize: '13px',
                    fontWeight: '600',
                    background: isApplied ? '#dcfce7' : isApplying ? '#e5e7eb' : '#6366f1',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isApplying || isApplied ? 'not-allowed' : 'pointer',
                    color: isApplied ? '#166534' : 'white',
                    marginTop: '2px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  title={isApplied ? 'Appliqué' : isApplying ? 'Application en cours...' : 'Appliquer dans Google Ads'}
                >
                  {isApplying ? (
                    <span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  ) : isApplied ? (
                    '✓ Appliqué'
                  ) : (
                    'Appliquer'
                  )}
                </button>
              );
            })()}
          </>
          );
        })()}
      </div>
    );
  };

  // Badge de type de campagne
  const getCampaignTypeBadge = (type) => {
    const typeMapping = {
      // Codes numériques
      2: { label: 'SEARCH', bg: '#dbeafe', color: '#1e40af' },
      3: { label: 'DISPLAY', bg: '#fce7f3', color: '#9d174d' },
      4: { label: 'SHOPPING', bg: '#fef3c7', color: '#92400e' },
      5: { label: 'HOTEL', bg: '#f0fdf4', color: '#166534' },
      6: { label: 'VIDEO', bg: '#fef2f2', color: '#991b1b' },
      7: { label: 'MULTI', bg: '#f5f3ff', color: '#5b21b6' },
      8: { label: 'LOCAL', bg: '#ecfdf5', color: '#047857' },
      9: { label: 'SMART', bg: '#fff7ed', color: '#c2410c' },
      10: { label: 'PMAX', bg: '#e0e7ff', color: '#3730a3' },
      11: { label: 'LOCAL SVC', bg: '#f0fdfa', color: '#0f766e' },
      12: { label: 'DISCOVERY', bg: '#fdf4ff', color: '#a21caf' },
      13: { label: 'TRAVEL', bg: '#f0f9ff', color: '#0369a1' },
      14: { label: 'DEMAND GEN', bg: '#fdf2f8', color: '#be185d' },
      // Noms textuels
      'SEARCH': { label: 'SEARCH', bg: '#dbeafe', color: '#1e40af' },
      'DISPLAY': { label: 'DISPLAY', bg: '#fce7f3', color: '#9d174d' },
      'SHOPPING': { label: 'SHOPPING', bg: '#fef3c7', color: '#92400e' },
      'HOTEL': { label: 'HOTEL', bg: '#f0fdf4', color: '#166534' },
      'VIDEO': { label: 'VIDEO', bg: '#fef2f2', color: '#991b1b' },
      'MULTI_CHANNEL': { label: 'MULTI', bg: '#f5f3ff', color: '#5b21b6' },
      'LOCAL': { label: 'LOCAL', bg: '#ecfdf5', color: '#047857' },
      'SMART': { label: 'SMART', bg: '#fff7ed', color: '#c2410c' },
      'PERFORMANCE_MAX': { label: 'PMAX', bg: '#e0e7ff', color: '#3730a3' },
      'LOCAL_SERVICES': { label: 'LOCAL SVC', bg: '#f0fdfa', color: '#0f766e' },
      'DISCOVERY': { label: 'DISCOVERY', bg: '#fdf4ff', color: '#a21caf' },
      'DEMAND_GEN': { label: 'DEMAND GEN', bg: '#fdf2f8', color: '#be185d' },
      'TRAVEL': { label: 'TRAVEL', bg: '#f0f9ff', color: '#0369a1' }
    };

    const style = typeMapping[type];
    if (!style) return null;

    return (
      <span style={{
        display: 'inline-flex',
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: '500',
        borderRadius: '4px',
        backgroundColor: style.bg,
        color: style.color,
        marginLeft: '8px'
      }}>
        {style.label}
      </span>
    );
  };

  if (error && !data.length) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="bid-optimizer-tool" style={{ width: '100%', padding: '0' }}>
        {/* En-tête */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                margin: '0 0 8px 0'
              }}>
                <BarChart3 style={{ marginRight: '8px', height: '24px', width: '24px', color: '#333333' }} />
                Optimiseur d'Enchères
              </h2>
              <p style={{ color: '#6b7280', margin: '0', fontSize: '16px' }}>
                Identifiez les opportunités d'ajustement d'enchères basées sur les performances et la part d'impression
              </p>
            </div>
            {selectedAccount && (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    border: hasCustomSettings && !showFilters ? '1px solid #16a34a' : '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: showFilters ? '#f3f4f6' : hasCustomSettings ? '#f0fdf4' : 'white',
                    color: hasCustomSettings && !showFilters ? '#166534' : '#374151',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <Settings style={{ height: '16px', width: '16px', marginRight: '8px' }} />
                  {hasCustomSettings ? 'Paramètres personnalisés' : 'Paramètres'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sélecteur de compte */}
        {/* Sans compte, la carte du sélecteur n'a rien à encadrer : on affiche
            l'état vide seul, plutôt que de le loger dans un cadre dans un cadre. */}
        {!loadingAccounts && accounts.length === 0 ? (
          <AucunCompteGoogleAds token={token} />
        ) : (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#111827', margin: '0 0 12px 0' }}>
              Compte Google Ads
            </h3>

            {loadingAccounts ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{
                  display: 'inline-block',
                  width: '20px',
                  height: '20px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #333333',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ margin: '8px 0 0 0', color: '#6b7280' }}>Chargement des comptes...</p>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', minWidth: '100px' }}>
                  Sélectionner :
                </label>
                <select
                  value={selectedAccount}
                  onChange={(e) => {
                    setSelectedAccount(e.target.value);
                    setData([]);
                    setStats(null);
                    // Réinitialiser les filtres
                    setRecommendationFilter(null);
                    setPriorityFilter(null);
                    setSettings(prev => ({ ...prev, campaignType: 'ALL', biddingStrategy: 'ALL' }));
                    // Mettre à jour l'URL avec le compte sélectionné
                    if (updateUrlWithAccount) updateUrlWithAccount(e.target.value || null);
                  }}
                  style={{
                    flex: '1',
                    minWidth: '300px',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: 'white'
                  }}
                >
                  <option value="">-- Choisir un compte --</option>
                  {accounts.map(account => {
                    const accountId = account.sourceId || account.id;
                    const accountName = account.name || account.displayName;
                    // Éviter de dupliquer l'ID si déjà présent dans le nom
                    const displayName = accountName.includes(accountId) ? accountName : `${accountName} (${accountId})`;
                    return (
                      <option key={accountId} value={accountId}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Panneau de paramètres */}
        {showFilters && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '16px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '500', color: '#111827', margin: '0' }}>
                Paramètres d'analyse
              </h3>
              <button
                onClick={() => { setSettings({...savedSettings}); setShowFilters(false); }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Fermer et annuler les modifications"
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{ flex: '0 0 160px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Période d'analyse
                </label>
                <select
                  value={settings.period}
                  onChange={(e) => setSettings({...settings, period: e.target.value})}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                >
                  <option value="TODAY">Aujourd'hui</option>
                  <option value="LAST_3_DAYS">3 derniers jours</option>
                  <option value="LAST_7_DAYS">7 derniers jours</option>
                  <option value="LAST_14_DAYS">14 derniers jours</option>
                  <option value="LAST_30_DAYS">30 derniers jours</option>
                  <option value="LAST_60_DAYS">60 derniers jours</option>
                  <option value="LAST_90_DAYS">90 derniers jours</option>
                </select>
              </div>

              <div style={{ flex: '0 0 160px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Agressivité
                </label>
                <select
                  value={settings.aggressiveness}
                  onChange={(e) => setSettings({...settings, aggressiveness: e.target.value})}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                >
                  <option value="conservative">Prudent (5-10%)</option>
                  <option value="moderate">Modéré (10-20%)</option>
                  <option value="aggressive">Agressif (15-30%)</option>
                </select>
              </div>

              <div style={{ flex: '0 0 160px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Conversions min.
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.minConversions}
                  onChange={(e) => setSettings({...settings, minConversions: parseInt(e.target.value) || 0})}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  placeholder="5"
                />
                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', marginBottom: '0' }}>
                  Seuil min. pour recommandations
                </p>
              </div>

              <div style={{ flex: '0 0 180px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Clics sans conv.
                </label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={settings.minClicksNoConversion}
                  onChange={(e) => setSettings({...settings, minClicksNoConversion: parseInt(e.target.value) || 0})}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  placeholder="50"
                />
                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', marginBottom: '0' }}>
                  Alerte si 0 conv. (0 = désactivé)
                </p>
              </div>

              <div style={{ flex: '0 0 180px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Coût sans conv. (€)
                </label>
                <input
                  type="number"
                  min="0"
                  max="10000"
                  value={settings.minCostNoConversion}
                  onChange={(e) => setSettings({...settings, minCostNoConversion: parseInt(e.target.value) || 0})}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  placeholder="30"
                />
                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', marginBottom: '0' }}>
                  Alerte si 0 conv. (0 = désactivé)
                </p>
              </div>

              <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-start', paddingTop: '20px' }}>
                <button
                  onClick={() => { setSavedSettings({...settings}); fetchData(); setShowFilters(false); }}
                  disabled={!selectedAccount || !hasUnsavedChanges}
                  style={{
                    padding: '8px 20px',
                    background: (selectedAccount && hasUnsavedChanges) ? '#333333' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (selectedAccount && hasUnsavedChanges) ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    height: '38px'
                  }}
                >
                  Sauvegarder les paramètres
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Écran de chargement */}
        {selectedAccount && loading && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '60px 24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'inline-block',
              width: '48px',
              height: '48px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #333333',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '16px'
            }}></div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
              Analyse en cours...
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0' }}>
              Récupération des métriques de performance et de part d'impression
            </p>
          </div>
        )}

        {/* Statistiques */}
        {selectedAccount && !loading && stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div
              onClick={() => { setRecommendationFilter(null); setPriorityFilter(null); }}
              style={{
                background: !recommendationFilter && !priorityFilter ? '#f0f9ff' : 'white',
                borderRadius: '12px',
                padding: '14px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: !recommendationFilter && !priorityFilter ? '2px solid #333333' : '2px solid transparent',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ padding: '8px', background: '#dbeafe', borderRadius: '8px', marginRight: '12px' }}>
                  <BarChart3 style={{ height: '20px', width: '20px', color: '#333333' }} />
                </div>
                <div>
                  <p style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', margin: '0' }}>Total analysé</p>
                  <p style={{ fontSize: '24px', fontWeight: '600', color: '#111827', margin: '0' }}>{effectiveStats.total}</p>
                </div>
              </div>
            </div>

            <div
              onClick={() => { setRecommendationFilter(recommendationFilter === 'increase' ? null : 'increase'); setPriorityFilter(null); }}
              style={{
                background: recommendationFilter === 'increase' ? '#dcfce7' : 'white',
                borderRadius: '12px',
                padding: '14px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: recommendationFilter === 'increase' ? '2px solid #16a34a' : '2px solid transparent',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ padding: '8px', background: '#dcfce7', borderRadius: '8px', marginRight: '12px' }}>
                  <TrendingUp style={{ height: '20px', width: '20px', color: '#16a34a' }} />
                </div>
                <div>
                  <p style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', margin: '0' }}>Augmenter</p>
                  <p style={{ fontSize: '24px', fontWeight: '600', color: '#16a34a', margin: '0' }}>{effectiveStats.increase}</p>
                </div>
              </div>
            </div>

            <div
              onClick={() => { setRecommendationFilter(recommendationFilter === 'decrease' ? null : 'decrease'); setPriorityFilter(null); }}
              style={{
                background: recommendationFilter === 'decrease' ? '#fee2e2' : 'white',
                borderRadius: '12px',
                padding: '14px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: recommendationFilter === 'decrease' ? '2px solid #dc2626' : '2px solid transparent',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ padding: '8px', background: '#fee2e2', borderRadius: '8px', marginRight: '12px' }}>
                  <TrendingDown style={{ height: '20px', width: '20px', color: '#dc2626' }} />
                </div>
                <div>
                  <p style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', margin: '0' }}>Diminuer</p>
                  <p style={{ fontSize: '24px', fontWeight: '600', color: '#dc2626', margin: '0' }}>{effectiveStats.decrease}</p>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Tableau des recommandations */}
        {selectedAccount && !loading && data.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '12px 16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '500', color: '#111827', margin: '0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Layers style={{ width: '18px', height: '18px' }} />
                Vue Hiérarchique
                <span style={{ fontSize: '12px', fontWeight: '400', color: '#6b7280', marginLeft: '4px' }}>
                  {data.length} campagnes • {Object.values(adgroupsData).reduce((sum, arr) => sum + arr.length, 0)} adgroups
                </span>
                <span style={{ fontSize: '12px', fontWeight: '400', color: '#6b7280', marginLeft: '8px' }}>
                  Métriques sur {settings.period === 'TODAY' ? '1' : settings.period === 'LAST_3_DAYS' ? '3' : settings.period === 'LAST_7_DAYS' ? '7' : settings.period === 'LAST_14_DAYS' ? '14' : settings.period === 'LAST_30_DAYS' ? '30' : settings.period === 'LAST_60_DAYS' ? '60' : '90'} jours
                </span>
              </h3>

              {/* Filtres d'affichage */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      padding: '6px 10px 6px 32px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      width: '180px',
                      background: searchTerm ? '#dbeafe' : 'white'
                    }}
                  />
                  <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                </div>
                <select
                  value={settings.granularity}
                  onChange={(e) => setSettings({...settings, granularity: e.target.value})}
                  style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: 'white' }}
                >
                  <option value="hierarchical">Tous niveaux</option>
                  <option value="campaign">Par campagne</option>
                  <option value="adgroup">Par groupe d'annonces</option>
                </select>

                <select
                  value={settings.campaignType}
                  onChange={(e) => setSettings({...settings, campaignType: e.target.value})}
                  style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: settings.campaignType !== 'ALL' ? '#dbeafe' : 'white' }}
                >
                  <option value="ALL">Tous types</option>
                  {availableCampaignTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>

                <select
                  value={settings.biddingStrategy}
                  onChange={(e) => setSettings({...settings, biddingStrategy: e.target.value})}
                  style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: settings.biddingStrategy !== 'ALL' ? '#dbeafe' : 'white' }}
                >
                  <option value="ALL">Toutes stratégies</option>
                  {availableBiddingStrategies.map(strategy => (
                    <option key={strategy.value} value={strategy.value}>{strategy.label}</option>
                  ))}
                </select>

                {(recommendationFilter || priorityFilter || settings.biddingStrategy !== 'ALL' || settings.campaignType !== 'ALL' || searchTerm) && (
                  <button
                    onClick={() => { setRecommendationFilter(null); setPriorityFilter(null); setSearchTerm(''); setSettings({...settings, biddingStrategy: 'ALL', campaignType: 'ALL'}); }}
                    style={{
                      padding: '6px 12px',
                      background: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    Réinitialiser
                  </button>
                )}
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', tableLayout: 'fixed' }}>
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th onClick={() => handleSort('name')} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: sortColumn === 'name' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '180px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      {settings.granularity === 'campaign' ? 'Campagne' : 'Groupe'} {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('clicks')} style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: sortColumn === 'clicks' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '55px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      Clics {sortColumn === 'clicks' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('conversionRate')} style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: sortColumn === 'conversionRate' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '55px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      % conv. {sortColumn === 'conversionRate' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('conversions')} style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: sortColumn === 'conversions' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '55px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      Conv. {sortColumn === 'conversions' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('cpc')} style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: sortColumn === 'cpc' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '55px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      CPC {sortColumn === 'cpc' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('cost')} style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: sortColumn === 'cost' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '65px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      Coût {sortColumn === 'cost' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('cpa')} style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: sortColumn === 'cpa' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '60px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      CPA {sortColumn === 'cpa' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('cos')} style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: sortColumn === 'cos' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '60px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      COS {sortColumn === 'cos' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('roas')} style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: sortColumn === 'roas' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '60px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      ROAS {sortColumn === 'roas' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('impressionShare')} style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: sortColumn === 'impressionShare' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '50px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      IS {sortColumn === 'impressionShare' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '140px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      Diagnostic
                    </th>
                    <th onClick={() => handleSort('daysSinceLastChange')} style={{ padding: '10px 8px', textAlign: 'center', fontSize: '13px', fontWeight: '500', color: sortColumn === 'daysSinceLastChange' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '75px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                      Dern. modif {sortColumn === 'daysSinceLastChange' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('recommendation')} style={{ padding: '10px 12px', textAlign: 'center', fontSize: '13px', fontWeight: '500', color: sortColumn === 'recommendation' ? '#333333' : '#6b7280', borderBottom: '1px solid #e5e7eb', cursor: 'pointer', width: '170px', whiteSpace: 'normal', lineHeight: '1.3', verticalAlign: 'middle' }}>
                      Reco {sortColumn === 'recommendation' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: 'white' }}>
                  {/* Vue hiérarchique */}
                  {settings.granularity === 'hierarchical' && filteredHierarchicalData.map((campaign, index) => {
                    const isExpanded = expandedCampaigns.has(campaign.campaignId);
                    return (
                      <React.Fragment key={campaign.campaignId || index}>
                      <tr
                        style={{
                          backgroundColor: getEffectiveRecommendationColor(campaign) === 'increase' ? '#dcfce7'
                            : getEffectiveRecommendationColor(campaign) === 'decrease' ? '#fee2e2'
                            : index % 2 === 0 ? 'white' : '#f9fafb',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f9ff'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getEffectiveRecommendationColor(campaign) === 'increase' ? '#dcfce7'
                          : getEffectiveRecommendationColor(campaign) === 'decrease' ? '#fee2e2'
                          : index % 2 === 0 ? 'white' : '#f9fafb'}
                      >
                        <td style={{ padding: '10px 12px', fontSize: '14px', fontWeight: '500', color: '#111827', whiteSpace: 'normal', wordBreak: 'break-word', verticalAlign: 'top' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            {/* Chevron pour déplier/replier les adgroups ou asset groups (PMAX) */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCampaignExpand(campaign.campaignId, campaign.campaignType, campaign.campaignName);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: '2px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title={isExpanded ? 'Replier' : (campaign.campaignType === 'PERFORMANCE_MAX' || campaign.campaignType === 'PMAX' || campaign.campaignType === 10 || campaign.campaignType === '10' ? 'Voir les groupes de composants' : 'Voir les groupes d\'annonces')}
                            >
                              {loadingAdgroups.has(campaign.campaignId) ? (
                                <span style={{ width: '16px', height: '16px', display: 'inline-block', border: '2px solid #e5e7eb', borderTopColor: '#333333', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                              ) : isExpanded ? (
                                <ChevronDown style={{ width: '16px', height: '16px', color: '#333333' }} />
                              ) : (
                                <ChevronRight style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                              )}
                            </button>
                            <div style={{ flex: 1 }} onClick={() => fetchCampaignHistory(campaign)}>
                              {/* Nom de la campagne + badge type */}
                              <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '4px' }}>
                                <span>{campaign.campaignName}</span>
                                {getCampaignTypeBadge(campaign.campaignType)}
                              </div>
                              {/* Informations de stratégie */}
                              <div style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                                {campaign.isPortfolioStrategy ? (
                                  <>
                                    <div>Stratégie de portefeuille: {campaign.portfolioStrategy?.name}</div>
                                    <div>
                                      {(() => {
                                        const stratType = String(campaign.portfolioStrategy?.type || '');
                                        const isRoasStrategy = stratType.includes('ROAS') || stratType.includes('CONVERSION_VALUE') || [8, 11].includes(campaign.portfolioStrategy?.type);
                                        const isCpaStrategy = stratType.includes('CPA') || stratType.includes('CONVERSIONS') || [2, 6].includes(campaign.portfolioStrategy?.type);

                                        if (campaign.portfolioStrategy?.targetValue) {
                                          return isRoasStrategy
                                            ? `ROAS cible: ${(campaign.portfolioStrategy.targetValue * 100).toFixed(0)}%`
                                            : `CPA cible: ${formatCurrency(campaign.portfolioStrategy.targetValue)}`;
                                        } else if (campaign.targetCpa) {
                                          return `CPA cible: ${formatCurrency(campaign.targetCpa)}`;
                                        } else if (campaign.targetRoas) {
                                          return `ROAS cible: ${(campaign.targetRoas * 100).toFixed(0)}%`;
                                        } else if (isCpaStrategy) {
                                          return 'CPA cible: non défini';
                                        } else if (isRoasStrategy) {
                                          return 'ROAS cible: non défini';
                                        }
                                        return campaign.biddingStrategyLabel || campaign.biddingStrategy || '';
                                      })()}
                                      {campaign.portfolioStrategy?.maxBidLimit && ` · CPC max: ${formatCurrency(campaign.portfolioStrategy.maxBidLimit)}`}
                                    </div>
                                  </>
                                ) : (
                                  <div>
                                    {campaign.targetRoas ? (
                                      `ROAS cible: ${(campaign.targetRoas * 100).toFixed(0)}%`
                                    ) : campaign.targetCpa ? (
                                      `CPA cible: ${formatCurrency(campaign.targetCpa)}`
                                    ) : (campaign.biddingStrategy === 15 || campaign.biddingStrategy === 'TARGET_IMPRESSION_SHARE') ? (
                                      <>
                                        {campaign.biddingStrategyLabel || 'Taux d\'impression cible'}
                                        {campaign.targetImpressionShareMaxCpc && ` · CPC max: ${formatCurrency(campaign.targetImpressionShareMaxCpc)}`}
                                      </>
                                    ) : (
                                      campaign.biddingStrategyLabel || campaign.biddingStrategy || ''
                                    )}
                                  </div>
                                )}
                              </div>
                              {/* Objectif personnalisé */}
                              <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                {customObjectives[campaign.campaignId] ? (
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    backgroundColor: '#dbeafe',
                                    color: '#1d4ed8',
                                    fontSize: '11px',
                                    fontWeight: '500'
                                  }}>
                                    🎯 Objectif : {customObjectives[campaign.campaignId].objectiveType === 'CPA'
                                      ? `CPA ${formatCurrency(customObjectives[campaign.campaignId].objectiveValue)}`
                                      : `ROAS ${customObjectives[campaign.campaignId].objectiveValue.toFixed(0)}%`
                                    }
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteCustomObjective(campaign.campaignId);
                                      }}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: '0 2px',
                                        cursor: 'pointer',
                                        color: '#1d4ed8',
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}
                                      title="Supprimer l'objectif personnalisé"
                                    >
                                      <X style={{ width: '12px', height: '12px' }} />
                                    </button>
                                  </span>
                                ) : null}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openObjectiveEditor(campaign.campaignId, campaign.campaignName, campaign.targetCpa, campaign.targetRoas);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    padding: '3px 8px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '12px',
                                    color: '#6b7280'
                                  }}
                                  title="Modifier l'objectif"
                                >
                                  <Edit2 style={{ width: '12px', height: '12px' }} />
                                  {customObjectives[campaign.campaignId] ? 'Modifier' : 'Objectif'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                          {campaign.clicks > 0 ? formatNumber(campaign.clicks) : '-'}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                          {campaign.clicks > 0 && campaign.conversions >= 0 ? `${((campaign.conversions / campaign.clicks) * 100).toFixed(1)}%` : '-'}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                          {campaign.conversions > 0 ? formatNumber(campaign.conversions, 1) : '-'}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                          {campaign.clicks > 0 ? `${(campaign.cost / campaign.clicks).toFixed(2)}€` : '-'}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                          {campaign.cost > 0 ? formatCurrency(campaign.cost) : '-'}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: (() => {
                          if (campaign.cpa <= 0) return '#6b7280';
                          const isCpaStrategy = campaign.targetCpa ||
                            campaign.biddingStrategyLabel?.includes('CPA') ||
                            campaign.biddingStrategy?.toString().includes('TARGET_CPA') ||
                            campaign.biddingStrategy?.toString().includes('MAXIMIZE_CONVERSIONS');
                          if (!isCpaStrategy) return '#6b7280';
                          const isCpaPortfolioStrategy = campaign.isPortfolioStrategy &&
                            (campaign.biddingStrategyLabel?.includes('CPA') || campaign.biddingStrategy?.toString().includes('TARGET_CPA'));
                          const effectiveTargetCpa = campaign.targetCpa || (isCpaPortfolioStrategy ? campaign.portfolioStrategy?.targetValue : null) || settings.targetCpa;
                          return campaign.cpa <= effectiveTargetCpa ? '#16a34a' : '#dc2626';
                        })(), fontWeight: campaign.cpa > 0 ? '600' : '400', verticalAlign: 'top' }}>
                          {campaign.cpa > 0 ? `${campaign.cpa.toFixed(1)}€` : '-'}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                          {campaign.conversionsValue > 0 ? ((campaign.cost / campaign.conversionsValue) * 100).toFixed(0) + '%' : '-'}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: (() => {
                          if (campaign.roas <= 0) return '#6b7280';
                          const isRoasStrategy = campaign.targetRoas ||
                            campaign.biddingStrategyLabel?.includes('ROAS') ||
                            campaign.biddingStrategy?.toString().includes('TARGET_ROAS') ||
                            campaign.biddingStrategy?.toString().includes('MAXIMIZE_CONVERSION_VALUE');
                          if (!isRoasStrategy) return '#6b7280';
                          const isRoasPortfolioStrategy = campaign.isPortfolioStrategy &&
                            (campaign.biddingStrategyLabel?.includes('ROAS') || campaign.biddingStrategy?.toString().includes('TARGET_ROAS'));
                          const effectiveTargetRoas = campaign.targetRoas || (isRoasPortfolioStrategy ? campaign.portfolioStrategy?.targetValue : null) || settings.targetRoas;
                          return campaign.roas >= effectiveTargetRoas ? '#16a34a' : '#dc2626';
                        })(), fontWeight: campaign.roas > 0 ? '600' : '400', verticalAlign: 'top' }}>
                          {campaign.roas > 0 ? (campaign.roas * 100).toFixed(0) + '%' : '-'}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                          {formatPercent(campaign.impressionShare)}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280', whiteSpace: 'normal', verticalAlign: 'top', lineHeight: '1.3' }}>
                          {campaign.reason}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'center', verticalAlign: 'top' }}>
                          {getCooldownBadge(campaign)}
                        </td>
                        <td style={{ padding: '10px 8px', verticalAlign: 'top' }}>
                          {campaign.recommendation !== 'test' && getRecommendationBadge(campaign.recommendation, campaign.adjustmentRange, campaign)}
                        </td>
                      </tr>
                      {/* Adgroups de la campagne (si déployé) - pour campagnes non-PMAX */}
                      {isExpanded && campaign.campaignType !== 'PERFORMANCE_MAX' && campaign.campaignType !== 'PMAX' && campaign.campaignType !== 10 && campaign.campaignType !== '10' && adgroupsData[campaign.campaignId] && adgroupsData[campaign.campaignId].map((adgroup, adgroupIndex) => {
                        // Déterminer si c'est un adgroup Shopping ou Search (pour afficher le chevron)
                        const adgroupCampaignType = adgroup.type || adgroup.campaignType || campaign.campaignType;
                        const isShoppingAdgroup = adgroupCampaignType === 'SHOPPING' || adgroupCampaignType === 4 || adgroupCampaignType === '4';
                        const isSearchAdgroup = adgroupCampaignType === 'SEARCH' || adgroupCampaignType === 2 || adgroupCampaignType === '2';
                        const adgroupKey = `${campaign.campaignId}-${adgroup.id}`;
                        const isAdgroupExpanded = expandedAdgroups.has(adgroupKey);
                        const productGroups = productGroupsData[adgroupKey] || [];
                        const keywords = keywordsData[adgroupKey] || [];
                        const isLoadingPG = loadingProductGroups.has(adgroupKey);
                        const isLoadingKW = loadingKeywords.has(adgroupKey);

                        return (
                          <React.Fragment key={`adgroup-fragment-${adgroup.id || adgroupIndex}`}>
                        <tr
                          key={`adgroup-${adgroup.id || adgroupIndex}`}
                          style={{
                            backgroundColor: getEffectiveRecommendationColor(adgroup, campaign) === 'increase' ? '#dcfce7'
                              : getEffectiveRecommendationColor(adgroup, campaign) === 'decrease' ? '#fee2e2'
                              : '#fefce8',
                            borderBottom: '1px solid #fef3c7'
                          }}
                        >
                          <td style={{ padding: '10px 12px 10px 40px', fontSize: '13px', fontWeight: '500', color: '#92400e', verticalAlign: 'top' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                              {/* Chevron pour les adgroups Shopping ou Search */}
                              {(isShoppingAdgroup || isSearchAdgroup) ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleAdgroupExpand(campaign.campaignId, adgroup.id, adgroupCampaignType, campaign.biddingStrategy);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0',
                                    color: '#92400e',
                                    marginTop: '2px',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                  title={isAdgroupExpanded
                                    ? (isShoppingAdgroup ? 'Masquer les groupes de produits' : 'Masquer les mots-clés')
                                    : (isShoppingAdgroup ? 'Voir les groupes de produits' : 'Voir les mots-clés')}
                                >
                                  {isAdgroupExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </button>
                              ) : (
                                <span style={{ color: '#d97706', marginTop: '2px' }}>↳</span>
                              )}
                              {/* Pastille de statut */}
                              <span
                                style={{
                                  width: '10px',
                                  height: '10px',
                                  borderRadius: '50%',
                                  backgroundColor: (adgroup.status === 2 || adgroup.status === 'ENABLED') ? '#22c55e' : '#ef4444',
                                  border: '2px solid white',
                                  boxShadow: '0 0 0 1px ' + ((adgroup.status === 2 || adgroup.status === 'ENABLED') ? '#16a34a' : '#dc2626'),
                                  flexShrink: 0,
                                  marginTop: '4px'
                                }}
                                title={(adgroup.status === 2 || adgroup.status === 'ENABLED') ? 'Activé' : 'En pause'}
                              />
                              <div>
                                <span>{adgroup.adGroupName || adgroup.name}</span>
                                {/* Afficher l'objectif CPA ou ROAS de l'adgroup selon la stratégie */}
                                {/* Note: Pour Shopping ROAS, ne pas afficher car non modifiable au niveau adgroup */}
                                {(() => {
                                  // Détecter le type de stratégie
                                  const portfolioType = adgroup.portfolioStrategy?.type;
                                  const isRoasStrategy = adgroup.biddingStrategy === 8 || adgroup.biddingStrategy === 11 ||
                                    portfolioType === 8 || portfolioType === 11 ||
                                    portfolioType === 'TARGET_ROAS' || portfolioType === 'MAXIMIZE_CONVERSION_VALUE';
                                  const isCpaStrategy = adgroup.biddingStrategy === 2 || adgroup.biddingStrategy === 6 ||
                                    portfolioType === 2 || portfolioType === 6 ||
                                    portfolioType === 'TARGET_CPA' || portfolioType === 'MAXIMIZE_CONVERSIONS';

                                  // Pour Shopping, ne pas afficher ROAS cible car non modifiable au niveau adgroup
                                  const adgroupType = adgroup.type || adgroup.campaignType;
                                  const isShoppingAdgroup = adgroupType === 'SHOPPING' || adgroupType === 4 || adgroupType === '4';

                                  if (isRoasStrategy && adgroup.targetRoas && !isShoppingAdgroup) {
                                    return (
                                      <div style={{ fontSize: '11px', color: '#b45309', marginTop: '2px' }}>
                                        ROAS cible: {(adgroup.targetRoas * 100).toFixed(0)}%
                                      </div>
                                    );
                                  } else if (isCpaStrategy && (adgroup.adGroupTargetCpa || adgroup.targetCpa)) {
                                    return (
                                      <div style={{ fontSize: '11px', color: '#b45309', marginTop: '2px' }}>
                                        CPA cible: {formatCurrency(adgroup.adGroupTargetCpa || adgroup.targetCpa)}
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#92400e', verticalAlign: 'top' }}>
                            {adgroup.clicks > 0 ? formatNumber(adgroup.clicks) : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#92400e', verticalAlign: 'top' }}>
                            {adgroup.clicks > 0 && adgroup.conversions >= 0 ? `${((adgroup.conversions / adgroup.clicks) * 100).toFixed(1)}%` : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#92400e', verticalAlign: 'top' }}>
                            {adgroup.conversions > 0 ? formatNumber(adgroup.conversions, 1) : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#92400e', verticalAlign: 'top' }}>
                            {adgroup.clicks > 0 ? `${(adgroup.cost / adgroup.clicks).toFixed(2)}€` : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#92400e', verticalAlign: 'top' }}>
                            {adgroup.cost > 0 ? formatCurrency(adgroup.cost) : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#92400e', verticalAlign: 'top' }}>
                            {adgroup.cpa > 0 ? `${adgroup.cpa.toFixed(1)}€` : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#92400e', verticalAlign: 'top' }}>
                            {adgroup.conversionsValue > 0 ? ((adgroup.cost / adgroup.conversionsValue) * 100).toFixed(0) + '%' : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#92400e', verticalAlign: 'top' }}>
                            {adgroup.roas > 0 ? (adgroup.roas * 100).toFixed(0) + '%' : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#92400e', verticalAlign: 'top' }}>
                            {formatPercent(adgroup.impressionShare)}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '14px', color: '#92400e', whiteSpace: 'normal', verticalAlign: 'top', lineHeight: '1.3' }}>
                            {adgroup.reason}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', verticalAlign: 'top' }}>
                            {getCooldownBadge(adgroup)}
                          </td>
                          <td style={{ padding: '10px 8px', verticalAlign: 'top' }}>
                            {adgroup.recommendation !== 'test' && getRecommendationBadge(adgroup.recommendation, adgroup.adjustmentRange, adgroup, campaign)}
                          </td>
                        </tr>

                        {/* Groupes de produits pour les adgroups Shopping (si déployé) */}
                        {isShoppingAdgroup && isAdgroupExpanded && (
                          <>
                            {/* Chargement */}
                            {isLoadingPG && (
                              <tr style={{ backgroundColor: '#fef9e7' }}>
                                <td colSpan={13} style={{ padding: '10px 12px 10px 60px', fontSize: '12px', color: '#b45309' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span className="loading-spinner" style={{ width: '14px', height: '14px', border: '2px solid #fbbf24', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                                    Chargement des groupes de produits...
                                  </div>
                                </td>
                              </tr>
                            )}

                            {/* Lignes des groupes de produits */}
                            {!isLoadingPG && productGroups.map((pg, pgIndex) => {
                              const updateKey = `pg-${adgroup.id}-${pg.criterionId}`;
                              const isUpdating = updatingProductGroups.has(updateKey);
                              const isExcluded = pg.negative === true;
                              // Normaliser le type (peut être 'UNIT', 2, ou '2')
                              const isUnit = pg.type === 'UNIT' || pg.type === 2 || pg.type === '2';

                              return (
                                <tr
                                  key={`pg-${pg.criterionId || pgIndex}`}
                                  style={{
                                    backgroundColor: isExcluded ? '#fef2f2' : (isUnit ? '#fffbeb' : '#fffdf5'),
                                    borderBottom: '1px solid #fef3c7',
                                    opacity: isExcluded ? 0.6 : 1
                                  }}
                                >
                                  <td style={{
                                    padding: `8px 12px 8px ${60 + (pg.depth || 0) * 16}px`,
                                    fontSize: '12px',
                                    color: isExcluded ? '#9ca3af' : '#a16207'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      {/* Pastille de statut actif/exclu */}
                                      <span
                                        style={{
                                          width: '8px',
                                          height: '8px',
                                          borderRadius: '50%',
                                          backgroundColor: isExcluded ? '#ef4444' : '#22c55e',
                                          flexShrink: 0
                                        }}
                                        title={isExcluded ? 'Exclu' : 'Actif'}
                                      />
                                      <span>{getSubdivisionIcon(pg.subdivisionType)}</span>
                                      <span style={{ fontWeight: isUnit ? '500' : '400' }}>
                                        {pg.displayLabel || 'Tous les produits'}
                                      </span>
                                    </div>
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#a16207' }}>
                                    {pg.clicks > 0 ? formatNumber(pg.clicks) : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#a16207' }}>
                                    {pg.ctr > 0 ? pg.ctr.toFixed(1) + '%' : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#a16207' }}>
                                    {pg.conversions > 0 ? formatNumber(pg.conversions, 1) : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#a16207' }}>
                                    {pg.cpc > 0 ? pg.cpc.toFixed(2) + '€' : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#a16207' }}>
                                    {pg.cost > 0 ? formatCurrency(pg.cost) : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#a16207' }}>
                                    {pg.cpa > 0 ? pg.cpa.toFixed(1) + '€' : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#a16207' }}>
                                    {pg.conversionsValue > 0 && pg.cost > 0 ? ((pg.cost / pg.conversionsValue) * 100).toFixed(0) + '%' : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#a16207' }}>
                                    {pg.roas > 0 ? (pg.roas * 100).toFixed(0) + '%' : '-'}
                                  </td>
                                  {/* IS - vide pour les groupes de produits */}
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#a16207' }}>
                                    -
                                  </td>
                                  {/* Diagnostic - vide */}
                                  <td style={{ padding: '10px 8px', fontSize: '14px', color: '#a16207' }}>
                                    -
                                  </td>
                                  {/* Dern. modif */}
                                  <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '14px', color: '#a16207' }}>
                                    {(() => {
                                      if (!pg.lastModified) {
                                        return <span style={{ color: '#9ca3af' }}>—</span>;
                                      }
                                      const lastModDate = new Date(pg.lastModified);
                                      const now = new Date();
                                      // Normaliser au début du jour pour comparer les dates calendaires
                                      const lastModNormalized = new Date(lastModDate.getFullYear(), lastModDate.getMonth(), lastModDate.getDate());
                                      const nowNormalized = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                      const daysSince = Math.round((nowNormalized - lastModNormalized) / (1000 * 60 * 60 * 24));
                                      const dateDisplay = daysSince === 0 ? "Aujourd'hui"
                                        : daysSince === 1 ? 'Hier'
                                        : `Il y a ${daysSince} jours`;

                                      const handleClick = (e) => {
                                        e.stopPropagation();
                                        setSelectedChangeDetails({
                                          name: pg.displayLabel || 'Groupe de produits',
                                          daysSinceLastChange: daysSince,
                                          changeHistory: [{
                                            type: 'Modification enchère CPC',
                                            date: pg.lastModified,
                                            daysAgo: daysSince
                                          }]
                                        });
                                      };

                                      // Afficher la date en gras avec soulignement au survol
                                      return (
                                        <span
                                          onClick={handleClick}
                                          style={{
                                            fontSize: '12px',
                                            color: '#374151',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            textDecoration: 'none'
                                          }}
                                          title="Cliquer pour voir les détails"
                                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                        >
                                          {dateDisplay}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  {/* Colonne Reco - Badge + Actions pour UNIT uniquement */}
                                  <td style={{ padding: '8px', fontSize: '11px', textAlign: 'center' }}>
                                    {isExcluded ? (
                                      <span style={{
                                        fontSize: '10px',
                                        padding: '2px 8px',
                                        backgroundColor: '#fee2e2',
                                        borderRadius: '4px',
                                        color: '#dc2626',
                                        fontWeight: '600'
                                      }}>
                                        EXCLU
                                      </span>
                                    ) : isUnit ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                        {/* Badge de recommandation (sauf 'test') */}
                                        {pg.recommendation !== 'test' && getRecommendationBadge(pg.recommendation, pg.adjustmentRange, pg, campaign)}
                                        {/* Input CPC pour stratégies CPC manuel/optimisé */}
                                        {pg.canEditBid && (() => {
                                          // Calculer la valeur recommandée selon la recommandation
                                          const currentCpc = pg.cpcBid || 0;
                                          const adjustPercent = pg.adjustmentRange?.[1] || 20;
                                          let recommendedCpc = currentCpc;
                                          if (pg.recommendation === 'increase') {
                                            recommendedCpc = currentCpc * (1 + adjustPercent / 100);
                                          } else if (pg.recommendation === 'decrease') {
                                            recommendedCpc = currentCpc * (1 - adjustPercent / 100);
                                          }
                                          const pgKey = `${adgroup.id}-${pg.criterionId}`;
                                          const isApplied = appliedProductGroups.has(pgKey);
                                          const inputValue = productGroupInputValues[pgKey] ?? recommendedCpc.toFixed(2);

                                          return (
                                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                              <span style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>
                                                {currentCpc.toFixed(2)}€
                                              </span>
                                              <span style={{ fontSize: '14px', color: '#9ca3af' }}>→</span>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {isApplied ? (
                                                  <span style={{
                                                    padding: '4px 6px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#166534',
                                                    backgroundColor: '#dcfce7',
                                                    borderRadius: '4px',
                                                    minWidth: '65px',
                                                    textAlign: 'center'
                                                  }}>
                                                    {inputValue}
                                                  </span>
                                                ) : (
                                                  <input
                                                    type="text"
                                                    value={inputValue}
                                                    placeholder="CPC"
                                                    style={{
                                                      width: '65px',
                                                      padding: '4px 6px',
                                                      fontSize: '14px',
                                                      fontWeight: '600',
                                                      border: '1px solid #d1d5db',
                                                      borderRadius: '4px',
                                                      textAlign: 'center'
                                                    }}
                                                    onChange={(e) => {
                                                      setProductGroupInputValues(prev => ({
                                                        ...prev,
                                                        [pgKey]: e.target.value
                                                      }));
                                                    }}
                                                    disabled={isUpdating}
                                                  />
                                                )}
                                                <span style={{ fontSize: '14px', color: '#6b7280' }}>€</span>
                                              </div>
                                            </div>
                                            {/* Bouton Appliquer */}
                                            <button
                                              onClick={() => {
                                                const newValue = parseFloat((productGroupInputValues[pgKey] ?? recommendedCpc.toFixed(2)).replace(',', '.'));
                                                if (!isNaN(newValue)) {
                                                  updateProductGroupBid(adgroup.id, pg.criterionId, newValue, pg.displayLabel || 'Groupe de produits');
                                                }
                                              }}
                                              disabled={isUpdating || isApplied}
                                              style={{
                                                padding: '6px 14px',
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                background: isApplied ? '#dcfce7' : isUpdating ? '#e5e7eb' : '#6366f1',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: isUpdating || isApplied ? 'not-allowed' : 'pointer',
                                                color: isApplied ? '#166534' : 'white',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                              }}
                                            >
                                              {isUpdating ? (
                                                <span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                                              ) : isApplied ? (
                                                '✓ Appliqué'
                                              ) : (
                                                'Appliquer'
                                              )}
                                            </button>
                                          </div>
                                          );
                                        })()}
                                        {/* Bouton Exclure pour stratégies ROAS/CPA (sans CPC modifiable) */}
                                        {!pg.canEditBid && pg.canExclude && pg.recommendation === 'decrease' && (
                                          <button
                                            onClick={() => excludeProductGroup(adgroup.id, pg.criterionId, campaign.campaignId)}
                                            disabled={isUpdating}
                                            style={{
                                              padding: '3px 8px',
                                              fontSize: '10px',
                                              backgroundColor: isUpdating ? '#d1d5db' : '#fef2f2',
                                              color: isUpdating ? '#6b7280' : '#dc2626',
                                              border: '1px solid ' + (isUpdating ? '#d1d5db' : '#fecaca'),
                                              borderRadius: '4px',
                                              cursor: isUpdating ? 'not-allowed' : 'pointer',
                                              fontWeight: '500'
                                            }}
                                          >
                                            {isUpdating ? '...' : 'Exclure'}
                                          </button>
                                        )}
                                      </div>
                                    ) : (
                                      <span style={{ color: '#9ca3af', fontSize: '10px' }}>-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}

                            {/* Message si aucun groupe de produits */}
                            {!isLoadingPG && productGroups.length === 0 && (
                              <tr style={{ backgroundColor: '#fffbeb' }}>
                                <td colSpan={13} style={{ padding: '10px 12px 10px 60px', fontSize: '12px', color: '#a16207', fontStyle: 'italic' }}>
                                  Aucun groupe de produits trouvé
                                </td>
                              </tr>
                            )}
                          </>
                        )}

                        {/* Mots-clés pour les adgroups Search (si déployé) */}
                        {isSearchAdgroup && isAdgroupExpanded && (
                          <>
                            {/* Chargement */}
                            {isLoadingKW && (
                              <tr style={{ backgroundColor: '#FAF9F5' }}>
                                <td colSpan={13} style={{ padding: '10px 12px 10px 60px', fontSize: '12px', color: '#1e40af' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span className="loading-spinner" style={{ width: '14px', height: '14px', border: '2px solid #1a1a1a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                                    Chargement des mots-clés...
                                  </div>
                                </td>
                              </tr>
                            )}

                            {/* Lignes des mots-clés */}
                            {!isLoadingKW && keywords.map((kw, kwIndex) => {
                              // Statut: 2 = ENABLED, 3 = PAUSED
                              const isEnabled = kw.status === 'ENABLED' || kw.status === 2 || kw.status === '2';
                              const kwKey = `${adgroup.id}-${kw.id}`;
                              const isUpdating = updatingKeywords.has(`kw-${adgroup.id}-${kw.id}`);
                              const isApplied = appliedKeywords.has(kwKey);
                              // Utiliser effectiveCpcBid (enchère CPC) au lieu de cpc (CPC moyen des métriques)
                              const currentCpcBid = kw.effectiveCpcBid || 0;
                              const adjustPercent = kw.adjustmentRange?.[1] || 20;
                              let recommendedCpc = currentCpcBid;
                              if (kw.recommendation === 'increase') {
                                recommendedCpc = currentCpcBid * (1 + adjustPercent / 100);
                              } else if (kw.recommendation === 'decrease') {
                                recommendedCpc = currentCpcBid * (1 - adjustPercent / 100);
                              }
                              const inputValue = keywordInputValues[kwKey] ?? recommendedCpc.toFixed(2);

                              return (
                                <tr
                                  key={`kw-${kw.id || kwIndex}`}
                                  style={{
                                    backgroundColor: '#FAF9F5',
                                    borderBottom: '1px solid #dbeafe'
                                  }}
                                >
                                  <td style={{
                                    padding: '10px 12px 10px 60px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    color: '#1e40af'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <span style={{ color: '#1a1a1a' }}>↳</span>
                                      <span>{kw.text}</span>
                                      <span style={{
                                        fontSize: '10px',
                                        padding: '1px 4px',
                                        backgroundColor: '#dbeafe',
                                        borderRadius: '3px',
                                        color: '#1d4ed8'
                                      }}>
                                        {kw.matchType}
                                      </span>
                                    </div>
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#1e40af' }}>
                                    {kw.clicks > 0 ? formatNumber(kw.clicks) : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#1e40af' }}>
                                    {kw.ctr > 0 ? kw.ctr.toFixed(1) + '%' : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#1e40af' }}>
                                    {kw.conversions > 0 ? formatNumber(kw.conversions, 1) : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#1e40af' }}>
                                    {kw.effectiveCpcBid > 0 ? kw.effectiveCpcBid.toFixed(2) + '€' : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#1e40af' }}>
                                    {kw.cost > 0 ? formatCurrency(kw.cost) : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#1e40af' }}>
                                    {kw.cpa > 0 ? kw.cpa.toFixed(1) + '€' : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#1e40af' }}>
                                    {kw.conversionsValue > 0 && kw.cost > 0 ? ((kw.cost / kw.conversionsValue) * 100).toFixed(0) + '%' : '-'}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#1e40af' }}>
                                    {kw.roas > 0 ? (kw.roas * 100).toFixed(0) + '%' : '-'}
                                  </td>
                                  {/* IS */}
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#1e40af' }}>
                                    {kw.impressionShare > 0 ? kw.impressionShare.toFixed(0) + '%' : '-'}
                                  </td>
                                  {/* Diagnostic */}
                                  <td style={{ padding: '10px 8px', fontSize: '12px', color: '#1e40af', whiteSpace: 'normal', lineHeight: '1.3' }}>
                                    {kw.reason || '-'}
                                  </td>
                                  {/* Dern. modif - vide */}
                                  <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '14px', color: '#1e40af' }}>
                                    -
                                  </td>
                                  {/* Recommandation + modification CPC */}
                                  <td style={{ padding: '8px', fontSize: '11px', textAlign: 'center' }}>
                                    {kw.canEditBid && kw.recommendation && kw.recommendation !== 'test' ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                        {/* Badge de recommandation */}
                                        {getRecommendationBadge(kw.recommendation, kw.adjustmentRange, kw, campaign)}
                                        {/* Input CPC */}
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>
                                              {currentCpcBid.toFixed(2)}€
                                            </span>
                                            <span style={{ fontSize: '14px', color: '#9ca3af' }}>→</span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                              {isApplied ? (
                                                <span style={{
                                                  padding: '4px 6px',
                                                  fontSize: '14px',
                                                  fontWeight: '600',
                                                  color: '#166534',
                                                  backgroundColor: '#dcfce7',
                                                  borderRadius: '4px',
                                                  minWidth: '65px',
                                                  textAlign: 'center'
                                                }}>
                                                  {inputValue}
                                                </span>
                                              ) : (
                                                <input
                                                  type="text"
                                                  value={inputValue}
                                                  placeholder="CPC"
                                                  style={{
                                                    width: '65px',
                                                    padding: '4px 6px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '4px',
                                                    textAlign: 'center'
                                                  }}
                                                  onChange={(e) => {
                                                    setKeywordInputValues(prev => ({
                                                      ...prev,
                                                      [kwKey]: e.target.value
                                                    }));
                                                  }}
                                                  disabled={isUpdating}
                                                />
                                              )}
                                              <span style={{ fontSize: '14px', color: '#6b7280' }}>€</span>
                                            </div>
                                          </div>
                                          {/* Bouton Appliquer */}
                                          <button
                                            onClick={() => {
                                              const newValue = parseFloat((keywordInputValues[kwKey] ?? recommendedCpc.toFixed(2)).replace(',', '.'));
                                              if (!isNaN(newValue)) {
                                                updateKeywordCpc(adgroup.id, kw.id, newValue, kw.text || 'Mot-clé');
                                              }
                                            }}
                                            disabled={isUpdating || isApplied}
                                            style={{
                                              padding: '6px 14px',
                                              fontSize: '13px',
                                              fontWeight: '600',
                                              background: isApplied ? '#dcfce7' : isUpdating ? '#e5e7eb' : '#6366f1',
                                              border: 'none',
                                              borderRadius: '6px',
                                              cursor: isUpdating || isApplied ? 'not-allowed' : 'pointer',
                                              color: isApplied ? '#166534' : 'white',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '6px'
                                            }}
                                          >
                                            {isUpdating ? (
                                              <span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                                            ) : isApplied ? (
                                              '✓ Appliqué'
                                            ) : (
                                              'Appliquer'
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                    ) : kw.recommendation && kw.recommendation !== 'test' ? (
                                      // Badge seul si pas de modification CPC possible
                                      getRecommendationBadge(kw.recommendation, kw.adjustmentRange, kw, campaign)
                                    ) : (
                                      <span style={{ color: '#9ca3af', fontSize: '10px' }}>-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}

                            {/* Message si aucun mot-clé */}
                            {!isLoadingKW && keywords.length === 0 && (
                              <tr style={{ backgroundColor: '#FAF9F5' }}>
                                <td colSpan={13} style={{ padding: '10px 12px 10px 60px', fontSize: '12px', color: '#1e40af', fontStyle: 'italic' }}>
                                  Aucun mot-clé trouvé
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                        </React.Fragment>
                        );
                      })}
                      {/* Asset Groups de la campagne PMAX (si déployé) */}
                      {isExpanded && (campaign.campaignType === 'PERFORMANCE_MAX' || campaign.campaignType === 'PMAX' || campaign.campaignType === 10 || campaign.campaignType === '10') && assetGroupsData[campaign.campaignId] && assetGroupsData[campaign.campaignId].map((assetGroup, assetGroupIndex) => (
                        <tr
                          key={`assetgroup-${assetGroup.id || assetGroupIndex}`}
                          style={{
                            backgroundColor: '#f3e8ff',
                            borderBottom: '1px solid #e9d5ff'
                          }}
                        >
                          <td style={{ padding: '10px 12px 10px 40px', fontSize: '13px', fontWeight: '500', color: '#7c3aed', verticalAlign: 'top' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ color: '#8b5cf6' }}>↳</span>
                              {/* Pastille de statut */}
                              <span
                                style={{
                                  width: '10px',
                                  height: '10px',
                                  borderRadius: '50%',
                                  backgroundColor: (assetGroup.status === 2 || assetGroup.status === 'ENABLED') ? '#22c55e' : '#ef4444',
                                  border: '2px solid white',
                                  boxShadow: '0 0 0 1px ' + ((assetGroup.status === 2 || assetGroup.status === 'ENABLED') ? '#16a34a' : '#dc2626'),
                                  flexShrink: 0
                                }}
                                title={(assetGroup.status === 2 || assetGroup.status === 'ENABLED') ? 'Activé' : 'En pause'}
                              />
                              <span>{assetGroup.assetGroupName || assetGroup.name}</span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#7c3aed', verticalAlign: 'top' }}>
                            {assetGroup.clicks > 0 ? formatNumber(assetGroup.clicks) : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#7c3aed', verticalAlign: 'top' }}>
                            {assetGroup.clicks > 0 && assetGroup.conversions >= 0 ? `${((assetGroup.conversions / assetGroup.clicks) * 100).toFixed(1)}%` : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#7c3aed', verticalAlign: 'top' }}>
                            {assetGroup.conversions > 0 ? formatNumber(assetGroup.conversions, 1) : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#7c3aed', verticalAlign: 'top' }}>
                            {assetGroup.clicks > 0 ? `${(assetGroup.cost / assetGroup.clicks).toFixed(2)}€` : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#7c3aed', verticalAlign: 'top' }}>
                            {assetGroup.cost > 0 ? formatCurrency(assetGroup.cost) : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#7c3aed', verticalAlign: 'top' }}>
                            {assetGroup.cpa > 0 ? `${assetGroup.cpa.toFixed(1)}€` : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#7c3aed', verticalAlign: 'top' }}>
                            {assetGroup.conversionsValue > 0 ? ((assetGroup.cost / assetGroup.conversionsValue) * 100).toFixed(0) + '%' : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#7c3aed', verticalAlign: 'top' }}>
                            {assetGroup.roas > 0 ? (assetGroup.roas * 100).toFixed(0) + '%' : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '13px', color: '#7c3aed', verticalAlign: 'top' }}>
                            -
                          </td>
                          <td colSpan={3} style={{ padding: '10px 12px', fontSize: '12px', color: '#7c3aed', whiteSpace: 'normal', verticalAlign: 'top', lineHeight: '1.3' }}>
                            Groupe de composants
                          </td>
                        </tr>
                      ))}
                      {/* Message de chargement */}
                      {isExpanded && loadingAdgroups.has(campaign.campaignId) && (
                        <tr style={{ backgroundColor: (campaign.campaignType === 'PERFORMANCE_MAX' || campaign.campaignType === 'PMAX' || campaign.campaignType === 10 || campaign.campaignType === '10') ? '#f3e8ff' : '#fefce8' }}>
                          <td colSpan={13} style={{ padding: '12px 12px 12px 40px', fontSize: '13px', color: (campaign.campaignType === 'PERFORMANCE_MAX' || campaign.campaignType === 'PMAX' || campaign.campaignType === 10 || campaign.campaignType === '10') ? '#7c3aed' : '#92400e' }}>
                            {(campaign.campaignType === 'PERFORMANCE_MAX' || campaign.campaignType === 'PMAX' || campaign.campaignType === 10 || campaign.campaignType === '10') ? 'Chargement des groupes de composants...' : 'Chargement des groupes d\'annonces...'}
                          </td>
                        </tr>
                      )}
                      {/* Message si aucun adgroup (campagnes non-PMAX) */}
                      {isExpanded && campaign.campaignType !== 'PERFORMANCE_MAX' && campaign.campaignType !== 'PMAX' && campaign.campaignType !== 10 && campaign.campaignType !== '10' && !loadingAdgroups.has(campaign.campaignId) && adgroupsData[campaign.campaignId] && adgroupsData[campaign.campaignId].length === 0 && (
                        <tr style={{ backgroundColor: '#fefce8' }}>
                          <td colSpan={13} style={{ padding: '12px 12px 12px 40px', fontSize: '13px', color: '#92400e', fontStyle: 'italic' }}>
                            Aucun groupe d'annonces trouvé
                          </td>
                        </tr>
                      )}
                      {/* Message si aucun asset group (campagnes PMAX) */}
                      {isExpanded && (campaign.campaignType === 'PERFORMANCE_MAX' || campaign.campaignType === 'PMAX' || campaign.campaignType === 10 || campaign.campaignType === '10') && !loadingAdgroups.has(campaign.campaignId) && assetGroupsData[campaign.campaignId] && assetGroupsData[campaign.campaignId].length === 0 && (
                        <tr style={{ backgroundColor: '#f3e8ff' }}>
                          <td colSpan={13} style={{ padding: '12px 12px 12px 40px', fontSize: '13px', color: '#7c3aed', fontStyle: 'italic' }}>
                            Aucun groupe de composants trouvé
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}

                  {/* Vue classique (campagne ou adgroup) */}
                  {settings.granularity !== 'hierarchical' && filteredData.map((item, index) => (
                    <tr
                      key={item.id || index}
                      onClick={() => settings.granularity === 'campaign' && fetchCampaignHistory(item)}
                      style={{
                        backgroundColor: getEffectiveRecommendationColor(item) === 'increase' ? '#dcfce7'
                          : getEffectiveRecommendationColor(item) === 'decrease' ? '#fee2e2'
                          : index % 2 === 0 ? 'white' : '#f9fafb',
                        borderBottom: '1px solid #e5e7eb',
                        cursor: settings.granularity === 'campaign' ? 'pointer' : 'default',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => settings.granularity === 'campaign' && (e.currentTarget.style.backgroundColor = '#f0f9ff')}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getEffectiveRecommendationColor(item) === 'increase' ? '#dcfce7'
                        : getEffectiveRecommendationColor(item) === 'decrease' ? '#fee2e2'
                        : index % 2 === 0 ? 'white' : '#f9fafb'}
                    >
                      <td style={{ padding: '10px 12px', fontSize: '14px', fontWeight: '500', color: '#111827', whiteSpace: 'normal', wordBreak: 'break-word', verticalAlign: 'top' }}>
                        {/* Nom de la campagne/adgroup + badge type */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '4px' }}>
                          <span>{item.name || item.campaignName || item.adGroupName}</span>
                          {getCampaignTypeBadge(item.type || item.campaignType)}
                        </div>
                        {/* Nom de la campagne parente si granularité adgroup */}
                        {settings.granularity === 'adgroup' && item.campaignName && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            {item.campaignName}
                          </div>
                        )}
                        {/* Informations de stratégie */}
                        <div style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                          {item.isPortfolioStrategy ? (
                            <>
                              <div>Stratégie de portefeuille: {item.portfolioStrategy?.name}</div>
                              <div>
                                {(() => {
                                  const stratType = String(item.portfolioStrategy?.type || '');
                                  const isRoasStrategy = stratType.includes('ROAS') || stratType.includes('CONVERSION_VALUE') || [8, 11].includes(item.portfolioStrategy?.type);
                                  const isCpaStrategy = stratType.includes('CPA') || stratType.includes('CONVERSIONS') || [2, 6].includes(item.portfolioStrategy?.type);

                                  if (item.portfolioStrategy?.targetValue) {
                                    return isRoasStrategy
                                      ? `ROAS cible: ${(item.portfolioStrategy.targetValue * 100).toFixed(0)}%`
                                      : `CPA cible: ${formatCurrency(item.portfolioStrategy.targetValue)}`;
                                  } else if (item.targetCpa) {
                                    return `CPA cible: ${formatCurrency(item.targetCpa)}`;
                                  } else if (item.targetRoas) {
                                    return `ROAS cible: ${(item.targetRoas * 100).toFixed(0)}%`;
                                  } else if (isCpaStrategy) {
                                    return 'CPA cible: non défini';
                                  } else if (isRoasStrategy) {
                                    return 'ROAS cible: non défini';
                                  }
                                  return item.biddingStrategyLabel || item.biddingStrategy || '';
                                })()}
                                {item.portfolioStrategy?.maxBidLimit && ` · CPC max: ${formatCurrency(item.portfolioStrategy.maxBidLimit)}`}
                                {item.portfolioStrategy?.minBidLimit && ` · CPC min: ${formatCurrency(item.portfolioStrategy.minBidLimit)}`}
                              </div>
                            </>
                          ) : (
                            <div>
                              {item.targetRoas ? (
                                `ROAS cible: ${(item.targetRoas * 100).toFixed(0)}%`
                              ) : item.targetCpa ? (
                                `CPA cible: ${formatCurrency(item.targetCpa)}`
                              ) : (item.biddingStrategy === 15 || item.biddingStrategy === 'TARGET_IMPRESSION_SHARE') ? (
                                <>
                                  {item.biddingStrategyLabel || 'Taux d\'impression cible'}
                                  {item.targetImpressionShareMaxCpc && ` · CPC max: ${formatCurrency(item.targetImpressionShareMaxCpc)}`}
                                </>
                              ) : (
                                item.biddingStrategyLabel || item.biddingStrategy || ''
                              )}
                            </div>
                          )}
                        </div>
                        {/* Objectif personnalisé - vue flat */}
                        {settings.granularity === 'campaign' && (
                          <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                            {customObjectives[item.campaignId || item.id] ? (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                backgroundColor: '#dbeafe',
                                color: '#1d4ed8',
                                fontSize: '11px',
                                fontWeight: '500'
                              }}>
                                🎯 Objectif : {customObjectives[item.campaignId || item.id].objectiveType === 'CPA'
                                  ? `CPA ${formatCurrency(customObjectives[item.campaignId || item.id].objectiveValue)}`
                                  : `ROAS ${customObjectives[item.campaignId || item.id].objectiveValue.toFixed(0)}%`
                                }
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteCustomObjective(item.campaignId || item.id);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '0 2px',
                                    cursor: 'pointer',
                                    color: '#1d4ed8',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                  title="Supprimer l'objectif personnalisé"
                                >
                                  <X style={{ width: '12px', height: '12px' }} />
                                </button>
                              </span>
                            ) : null}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openObjectiveEditor(item.campaignId || item.id, item.name || item.campaignName, item.targetCpa, item.targetRoas);
                              }}
                              style={{
                                background: 'none',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                padding: '3px 8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '12px',
                                color: '#6b7280'
                              }}
                              title="Modifier l'objectif"
                            >
                              <Edit2 style={{ width: '12px', height: '12px' }} />
                              {customObjectives[item.campaignId || item.id] ? 'Modifier' : 'Objectif'}
                            </button>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                        {item.clicks > 0 ? formatNumber(item.clicks) : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                        {item.clicks > 0 && item.conversions >= 0 ? `${((item.conversions / item.clicks) * 100).toFixed(1)}%` : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                        {item.conversions > 0 ? formatNumber(item.conversions, 1) : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                        {item.cpc > 0 ? `${item.cpc.toFixed(2)}€` : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                        {item.cost > 0 ? formatCurrency(item.cost) : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: (() => {
                        if (item.cpa <= 0) return '#6b7280';
                        // Colorer le CPA uniquement si c'est une stratégie CPA
                        const isCpaStrategy = item.targetCpa ||
                          item.biddingStrategyLabel?.includes('CPA') ||
                          item.biddingStrategy?.toString().includes('TARGET_CPA') ||
                          item.biddingStrategy?.toString().includes('MAXIMIZE_CONVERSIONS');
                        if (!isCpaStrategy) return '#6b7280';
                        const isCpaPortfolioStrategy = item.isPortfolioStrategy &&
                          (item.biddingStrategyLabel?.includes('CPA') || item.biddingStrategy?.toString().includes('TARGET_CPA'));
                        const effectiveTargetCpa = item.targetCpa || (isCpaPortfolioStrategy ? item.portfolioStrategy?.targetValue : null) || settings.targetCpa;
                        return item.cpa <= effectiveTargetCpa ? '#16a34a' : '#dc2626';
                      })(), fontWeight: (() => {
                        if (item.cpa <= 0) return '400';
                        const isCpaStrategy = item.targetCpa ||
                          item.biddingStrategyLabel?.includes('CPA') ||
                          item.biddingStrategy?.toString().includes('TARGET_CPA') ||
                          item.biddingStrategy?.toString().includes('MAXIMIZE_CONVERSIONS');
                        if (!isCpaStrategy) return '400';
                        const isCpaPortfolioStrategy = item.isPortfolioStrategy &&
                          (item.biddingStrategyLabel?.includes('CPA') || item.biddingStrategy?.toString().includes('TARGET_CPA'));
                        const effectiveTargetCpa = item.targetCpa || (isCpaPortfolioStrategy ? item.portfolioStrategy?.targetValue : null) || settings.targetCpa;
                        return item.cpa <= effectiveTargetCpa ? '600' : '400';
                      })(), verticalAlign: 'top' }}>
                        {item.cpa > 0 ? `${item.cpa.toFixed(1)}€` : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                        {item.conversionsValue > 0 ? ((item.cost / item.conversionsValue) * 100).toFixed(0) + '%' : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: (() => {
                        if (item.roas <= 0) return '#6b7280';
                        // Colorer le ROAS uniquement si c'est une stratégie ROAS
                        const isRoasStrategy = item.targetRoas ||
                          item.biddingStrategyLabel?.includes('ROAS') ||
                          item.biddingStrategy?.toString().includes('TARGET_ROAS') ||
                          item.biddingStrategy?.toString().includes('MAXIMIZE_CONVERSION_VALUE');
                        if (!isRoasStrategy) return '#6b7280';
                        const isRoasPortfolioStrategy = item.isPortfolioStrategy &&
                          (item.biddingStrategyLabel?.includes('ROAS') || item.biddingStrategy?.toString().includes('TARGET_ROAS'));
                        const effectiveTargetRoas = item.targetRoas || (isRoasPortfolioStrategy ? item.portfolioStrategy?.targetValue : null) || settings.targetRoas;
                        return item.roas >= effectiveTargetRoas ? '#16a34a' : '#dc2626';
                      })(), fontWeight: (() => {
                        if (item.roas <= 0) return '400';
                        const isRoasStrategy = item.targetRoas ||
                          item.biddingStrategyLabel?.includes('ROAS') ||
                          item.biddingStrategy?.toString().includes('TARGET_ROAS') ||
                          item.biddingStrategy?.toString().includes('MAXIMIZE_CONVERSION_VALUE');
                        if (!isRoasStrategy) return '400';
                        const isRoasPortfolioStrategy = item.isPortfolioStrategy &&
                          (item.biddingStrategyLabel?.includes('ROAS') || item.biddingStrategy?.toString().includes('TARGET_ROAS'));
                        const effectiveTargetRoas = item.targetRoas || (isRoasPortfolioStrategy ? item.portfolioStrategy?.targetValue : null) || settings.targetRoas;
                        return item.roas >= effectiveTargetRoas ? '600' : '400';
                      })(), verticalAlign: 'top' }}>
                        {item.roas > 0 ? (item.roas * 100).toFixed(0) + '%' : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '14px', color: '#6b7280', verticalAlign: 'top' }}>
                        {formatPercent(item.impressionShare)}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280', whiteSpace: 'normal', verticalAlign: 'top', lineHeight: '1.3' }}>
                        {item.reason}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center', verticalAlign: 'top' }}>
                        {getCooldownBadge(item)}
                      </td>
                      <td style={{ padding: '10px 8px', verticalAlign: 'top' }}>
                        {item.recommendation !== 'test' && getRecommendationBadge(item.recommendation, item.adjustmentRange, item)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Message si aucune donnée */}
        {selectedAccount && !loading && data.length === 0 && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '60px 24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <BarChart3 style={{ margin: '0 auto 16px auto', height: '48px', width: '48px', color: '#9ca3af' }} />
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '500', color: '#111827' }}>
              Aucune donnée disponible
            </h3>
            <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
              Cliquez sur "Paramètres" pour configurer et récupérer les données
            </p>
          </div>
        )}

        {/* Modal détails de modification */}
        {selectedChangeDetails && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setSelectedChangeDetails(null)}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* En-tête du modal */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                    Historique des modifications ({selectedChangeDetails.changeHistory?.length || 0})
                  </h3>
                  <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
                    {selectedChangeDetails.name}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedChangeDetails(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <X size={20} color="#6b7280" />
                </button>
              </div>

              {/* Contenu du modal */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Historique des modifications */}
                <div>
                  {selectedChangeDetails.changeHistory && selectedChangeDetails.changeHistory.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {selectedChangeDetails.changeHistory.map((change, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '12px 16px',
                            backgroundColor: change.isPortfolioStrategy ? '#faf5ff' : (index === 0 ? '#f0f9ff' : '#f9fafb'),
                            borderRadius: '8px',
                            border: change.isPortfolioStrategy ? '1px solid #e9d5ff' : (index === 0 ? '1px solid #bfdbfe' : '1px solid #e5e7eb')
                          }}
                        >
                          {/* Badge stratégie de portefeuille */}
                          {change.isPortfolioStrategy && (
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 6px',
                              fontSize: '10px',
                              fontWeight: '500',
                              borderRadius: '4px',
                              backgroundColor: '#f3e8ff',
                              color: '#7c3aed',
                              marginBottom: '8px'
                            }}>
                              📁 Stratégie de portefeuille
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                            <span style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: change.isPortfolioStrategy ? '#7c3aed' : '#111827'
                            }}>
                              {change.type}
                            </span>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: '500',
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              backgroundColor: change.daysAgo < 7 ? '#fee2e2' : change.daysAgo <= 14 ? '#fef3c7' : '#f3f4f6',
                              color: change.daysAgo < 7 ? '#991b1b' : change.daysAgo <= 14 ? '#92400e' : '#6b7280'
                            }}>
                              {change.daysAgo === 0 ? "Aujourd'hui" : change.daysAgo === 1 ? 'Hier' : `Il y a ${change.daysAgo}j`}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                              {new Date(change.date).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            <span style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <User size={12} />
                              {change.user}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      padding: '20px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '13px'
                    }}>
                      Aucune modification enregistrée sur les 29 derniers jours
                    </div>
                  )}
                </div>
              </div>

              {/* Bouton fermer */}
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setSelectedChangeDetails(null)}
                  style={{
                    padding: '10px 20px',
                    background: '#333333',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Historique Campagne */}
        {selectedCampaign && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }} onClick={() => setSelectedCampaign(null)}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '1000px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }} onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
                    {selectedCampaign.name}
                  </h2>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
                    Évolution sur les 30 derniers jours
                  </p>
                </div>
                <button
                  onClick={() => setSelectedCampaign(null)}
                  style={{
                    padding: '8px',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: '#f3f4f6',
                    cursor: 'pointer'
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Contenu */}
              <div style={{ padding: '24px' }}>
                {loadingHistory ? (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      border: '3px solid #e5e7eb',
                      borderTopColor: '#333333',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 16px'
                    }} />
                    <p style={{ color: '#6b7280' }}>Chargement de l'historique...</p>
                  </div>
                ) : campaignHistory && campaignHistory.length > 0 ? (
                  <>
                    {/* Graphique */}
                    <div style={{ height: '400px', marginBottom: '24px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={(() => {
                          // Créer un Set des dates de modification pour comparaison rapide
                          const changeDates = new Set();
                          if (selectedCampaign.changeHistory) {
                            selectedCampaign.changeHistory.forEach(change => {
                              if (change.daysAgo <= 30) {
                                let changeDateFormatted;
                                if (change.date) {
                                  changeDateFormatted = new Date(change.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                                } else {
                                  const d = new Date();
                                  d.setDate(d.getDate() - change.daysAgo);
                                  changeDateFormatted = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                                }
                                changeDates.add(changeDateFormatted);
                              }
                            });
                          }

                          // Calcul de la régression linéaire pour la tendance ROAS
                          const n = campaignHistory.length;
                          const roasValues = campaignHistory.map(d => (d.roas || 0) * 100);
                          const sumX = (n * (n - 1)) / 2; // Somme de 0 à n-1
                          const sumY = roasValues.reduce((a, b) => a + b, 0);
                          const sumXY = roasValues.reduce((acc, y, x) => acc + x * y, 0);
                          const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6; // Somme des carrés de 0 à n-1

                          const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
                          const intercept = (sumY - slope * sumX) / n;

                          const chartData = campaignHistory.map((d, index) => {
                            const dateFormatted = new Date(d.date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                            const hasChange = changeDates.has(dateFormatted);

                            // Valeur de la ligne de tendance (régression linéaire)
                            const roasTrend = parseFloat((slope * index + intercept).toFixed(0));

                            return {
                              ...d,
                              dateFormatted,
                              roasPercent: parseFloat((d.roas * 100).toFixed(0)),
                              roasTrend, // Régression linéaire
                              is: d.impressionShare ? parseFloat(d.impressionShare.toFixed(1)) : null,
                              changeMarker: hasChange ? 100 : null // Marquer à 100% sur l'axe IS
                            };
                          });
                          return chartData;
                        })()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis
                            dataKey="dateFormatted"
                            tick={{ fontSize: 10, fill: '#6b7280' }}
                            tickLine={false}
                          />
                          {/* Axe gauche: IS, échelle fixe 0-100% */}
                          <YAxis
                            yAxisId="is"
                            orientation="left"
                            tick={{ fontSize: 10, fill: '#f59e0b' }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) => `${v}%`}
                            domain={[0, 100]}
                          />
                          {/* Axe droite: Clics */}
                          <YAxis
                            yAxisId="clicks"
                            orientation="right"
                            tick={{ fontSize: 10, fill: '#6366f1' }}
                            tickLine={false}
                            axisLine={false}
                          />
                          {/* Axe Conversions (caché, propre échelle réduite) */}
                          <YAxis
                            yAxisId="conversions"
                            orientation="right"
                            hide={true}
                            domain={[0, dataMax => dataMax * 3]}
                          />
                          {/* Axe ROAS (caché) */}
                          <YAxis
                            yAxisId="roas"
                            orientation="right"
                            hide={true}
                          />
                          <Tooltip
                            contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}
                            labelFormatter={(label) => label}
                            formatter={(value, name) => {
                              if (name === 'ROAS %') return [`${value}%`, 'ROAS'];
                              if (name === 'Tendance ROAS') return [value !== null ? `${value}%` : '-', 'Tendance (7j)'];
                              if (name === 'IS %') return [value !== null ? `${value}%` : '-', 'Part d\'impression'];
                              if (name === 'Conversions') return [parseFloat(value).toFixed(1), 'Conversions'];
                              if (name === 'Clics') return [value, 'Clics'];
                              if (name === 'Modification' || name === 'dateFormatted') return null;
                              return null;
                            }}
                          />
                          <Legend
                            wrapperStyle={{ fontSize: '11px' }}
                            payload={[
                              { value: 'Clics', type: 'line', color: '#6366f1' },
                              { value: 'Conversions', type: 'rect', color: '#3b82f6' },
                              { value: 'ROAS', type: 'line', color: '#16a34a' },
                              { value: 'Tendance (7j)', type: 'line', color: '#15803d' },
                              { value: 'ROAS cible', type: 'plainline', color: '#dc2626' },
                              { value: 'Modification', type: 'circle', color: '#7c3aed' },
                              { value: 'IS %', type: 'line', color: '#f59e0b' }
                            ]}
                          />
                          <Line
                            yAxisId="clicks"
                            type="monotone"
                            dataKey="clicks"
                            name="Clics"
                            stroke="#6366f1"
                            strokeWidth={1.5}
                            dot={false}
                          />
                          <Bar
                            yAxisId="conversions"
                            dataKey="conversions"
                            name="Conversions"
                            fill="#3b82f6"
                            opacity={0.7}
                            radius={[2, 2, 0, 0]}
                          />
                          <Line
                            yAxisId="roas"
                            type="monotone"
                            dataKey="roasPercent"
                            name="ROAS %"
                            stroke="#16a34a"
                            strokeWidth={1.5}
                            dot={{ r: 2 }}
                            activeDot={{ r: 4 }}
                            opacity={0.6}
                          />
                          {/* Ligne de tendance ROAS (régression linéaire) */}
                          <Line
                            yAxisId="roas"
                            type="linear"
                            dataKey="roasTrend"
                            name="Tendance ROAS"
                            stroke="#15803d"
                            strokeWidth={2}
                            strokeDasharray="6 3"
                            dot={false}
                            connectNulls={true}
                          />
                          {/* Indicateurs de modifications (Scatter) - après ROAS dans la légende */}
                          <Scatter
                            yAxisId="is"
                            dataKey="changeMarker"
                            name="Modification"
                            legendType="none"
                            fill="#7c3aed"
                            shape={(props) => {
                              if (!props.payload.changeMarker) return null;
                              const isHighlighted = hoveredChangeDate === props.payload.dateFormatted;
                              return (
                                <g style={{ transition: 'all 0.15s' }}>
                                  <line
                                    x1={props.cx}
                                    y1={0}
                                    x2={props.cx}
                                    y2={props.cy + 150}
                                    stroke={isHighlighted ? '#5b21b6' : '#7c3aed'}
                                    strokeWidth={isHighlighted ? 4 : 2}
                                    strokeDasharray={isHighlighted ? '0' : '4 4'}
                                  />
                                  <circle
                                    cx={props.cx}
                                    cy={20}
                                    r={isHighlighted ? 14 : 10}
                                    fill={isHighlighted ? '#5b21b6' : '#7c3aed'}
                                    style={{ filter: isHighlighted ? 'drop-shadow(0 0 6px #7c3aed)' : 'none' }}
                                  />
                                  <text x={props.cx} y={isHighlighted ? 26 : 24} textAnchor="middle" fill="white" fontSize={isHighlighted ? 16 : 12}>⚙</text>
                                </g>
                              );
                            }}
                          />
                          <Line
                            yAxisId="is"
                            type="monotone"
                            dataKey="is"
                            name="IS %"
                            stroke="#f59e0b"
                            strokeWidth={1.5}
                            strokeDasharray="4 2"
                            dot={false}
                            connectNulls={false}
                          />
                          {/* Ligne de référence pour le ROAS cible */}
                          {selectedCampaign.targetRoas && (
                            <ReferenceLine
                              yAxisId="roas"
                              y={selectedCampaign.targetRoas * 100}
                              stroke="#dc2626"
                              strokeDasharray="5 5"
                              label={{ value: `Cible ${(selectedCampaign.targetRoas * 100).toFixed(0)}%`, fill: '#dc2626', fontSize: 10, position: 'top' }}
                            />
                          )}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Historique des modifications */}
                    {selectedCampaign.changeHistory && selectedCampaign.changeHistory.length > 0 && (
                      <div style={{ marginTop: '24px' }}>
                        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Clock size={16} />
                          Historique des modifications
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {selectedCampaign.changeHistory.map((change, idx) => {
                            // Calculer la date formatée pour la comparaison
                            let changeDateFormatted;
                            if (change.date) {
                              changeDateFormatted = new Date(change.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                            } else {
                              const d = new Date();
                              d.setDate(d.getDate() - change.daysAgo);
                              changeDateFormatted = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                            }
                            const isHovered = hoveredChangeDate === changeDateFormatted;

                            return (
                              <div
                                key={idx}
                                onMouseEnter={() => setHoveredChangeDate(changeDateFormatted)}
                                onMouseLeave={() => setHoveredChangeDate(null)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '10px 14px',
                                  backgroundColor: isHovered ? '#ede9fe' : '#f9fafb',
                                  borderRadius: '8px',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.15s',
                                  border: isHovered ? '1px solid #7c3aed' : '1px solid transparent'
                                }}
                              >
                                <div style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: change.isPortfolioStrategy ? '#7c3aed' : '#333333',
                                  flexShrink: 0
                                }} />
                                <div style={{ flex: 1 }}>
                                  <span style={{ color: '#111827', fontWeight: '500' }}>{change.type}</span>
                                </div>
                                <div style={{ color: '#6b7280', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span>il y a {change.daysAgo}j</span>
                                  {change.user && <span style={{ color: '#9ca3af' }}>• {change.user}</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Résumé des KPIs */}
                    {(() => {
                      const totalClicks = campaignHistory.reduce((sum, d) => sum + (d.clicks || 0), 0);
                      const totalConversions = campaignHistory.reduce((sum, d) => sum + (d.conversions || 0), 0);
                      const totalCost = campaignHistory.reduce((sum, d) => sum + (d.cost || 0), 0);
                      const totalValue = campaignHistory.reduce((sum, d) => sum + (d.conversionsValue || 0), 0);
                      const validIS = campaignHistory.filter(d => d.impressionShare !== null && d.impressionShare !== undefined);
                      const avgIS = validIS.length > 0 ? validIS.reduce((sum, d) => sum + d.impressionShare, 0) / validIS.length : null;

                      const convRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
                      const cpc = totalClicks > 0 ? totalCost / totalClicks : 0;
                      const cpa = totalConversions > 0 ? totalCost / totalConversions : 0;
                      const cos = totalValue > 0 ? (totalCost / totalValue) * 100 : 0;
                      const roas = totalCost > 0 ? (totalValue / totalCost) * 100 : 0;

                      return (
                        <div style={{
                          marginTop: '24px',
                          padding: '16px',
                          backgroundColor: '#f0f9ff',
                          borderRadius: '12px',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(9, 1fr)',
                          gap: '12px'
                        }}>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Clics</p>
                            <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: '4px 0 0' }}>
                              {totalClicks.toLocaleString('fr-FR')}
                            </p>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>% conv.</p>
                            <p style={{ fontSize: '16px', fontWeight: '600', color: '#6366f1', margin: '4px 0 0' }}>
                              {convRate.toFixed(2)}%
                            </p>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Conv.</p>
                            <p style={{ fontSize: '16px', fontWeight: '600', color: '#333333', margin: '4px 0 0' }}>
                              {totalConversions.toFixed(1)}
                            </p>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>CPC</p>
                            <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: '4px 0 0' }}>
                              {cpc.toFixed(2)}€
                            </p>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Coût</p>
                            <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: '4px 0 0' }}>
                              {totalCost.toFixed(0)}€
                            </p>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>CPA</p>
                            <p style={{ fontSize: '16px', fontWeight: '600', color: '#dc2626', margin: '4px 0 0' }}>
                              {totalConversions > 0 ? cpa.toFixed(2) + '€' : '-'}
                            </p>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>COS</p>
                            <p style={{ fontSize: '16px', fontWeight: '600', color: '#ea580c', margin: '4px 0 0' }}>
                              {totalValue > 0 ? cos.toFixed(0) + '%' : '-'}
                            </p>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>ROAS</p>
                            <p style={{ fontSize: '16px', fontWeight: '600', color: '#16a34a', margin: '4px 0 0' }}>
                              {totalCost > 0 ? roas.toFixed(0) + '%' : '-'}
                            </p>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>IS</p>
                            <p style={{ fontSize: '16px', fontWeight: '600', color: '#f59e0b', margin: '4px 0 0' }}>
                              {avgIS !== null ? avgIS.toFixed(0) + '%' : '-'}
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
                    <Activity size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                    <p>Aucune donnée disponible pour cette période</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal d'édition de l'objectif */}
        {editingObjective && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setEditingObjective(null)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                width: '400px',
                maxWidth: '90%',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  Modifier l'objectif
                </h3>
                <button
                  onClick={() => setEditingObjective(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  <X style={{ width: '20px', height: '20px' }} />
                </button>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#6b7280' }}>
                  Campagne : <strong style={{ color: '#111827' }}>{editingObjective.campaignName}</strong>
                </p>
                {(editingObjective.targetCpa || editingObjective.targetRoas) && !customObjectives[editingObjective.campaignId] && (
                  <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#9ca3af' }}>
                    Objectif Google Ads actuel :
                    {editingObjective.targetCpa && ` CPA cible ${formatCurrency(editingObjective.targetCpa)}`}
                    {editingObjective.targetRoas && ` ROAS cible ${(editingObjective.targetRoas * 100).toFixed(0)}%`}
                  </p>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                  Type d'objectif
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setObjectiveForm(prev => ({ ...prev, type: 'CPA' }))}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: objectiveForm.type === 'CPA' ? '2px solid #333333' : '1px solid #d1d5db',
                      borderRadius: '8px',
                      backgroundColor: objectiveForm.type === 'CPA' ? '#FAF9F5' : 'white',
                      color: objectiveForm.type === 'CPA' ? '#333333' : '#6b7280',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    CPA cible
                  </button>
                  <button
                    onClick={() => setObjectiveForm(prev => ({ ...prev, type: 'ROAS' }))}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: objectiveForm.type === 'ROAS' ? '2px solid #333333' : '1px solid #d1d5db',
                      borderRadius: '8px',
                      backgroundColor: objectiveForm.type === 'ROAS' ? '#FAF9F5' : 'white',
                      color: objectiveForm.type === 'ROAS' ? '#333333' : '#6b7280',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ROAS cible
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                  {objectiveForm.type === 'CPA' ? 'CPA cible (€)' : 'ROAS cible (%)'}
                </label>
                <input
                  type="number"
                  value={objectiveModalValues.currentValue}
                  onChange={(e) => {
                    const field = objectiveForm.type === 'CPA' ? 'cpaValue' : 'roasValue';
                    setObjectiveForm(prev => ({ ...prev, [field]: e.target.value }));
                  }}
                  placeholder={objectiveForm.type === 'CPA' ? 'ex: 35.50' : 'ex: 150'}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  min="0"
                  step={objectiveForm.type === 'CPA' ? '0.01' : '1'}
                />
                <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#9ca3af' }}>
                  {objectiveForm.type === 'CPA'
                    ? 'Le coût par acquisition cible en euros'
                    : 'Le retour sur dépenses publicitaires cible en pourcentage (ex: 150 = 150%)'
                  }
                </p>
              </div>

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setEditingObjective(null)}
                  style={{
                    padding: '10px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={saveCustomObjective}
                  disabled={objectiveModalValues.isDisabled}
                  style={{
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: objectiveModalValues.isDisabled ? '#9ca3af' : '#333333',
                    color: 'white',
                    fontWeight: '500',
                    cursor: objectiveModalValues.isDisabled ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {savingObjective ? (
                    <>Sauvegarde...</>
                  ) : (
                    <>
                      <Check style={{ width: '16px', height: '16px' }} />
                      Enregistrer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default BidOptimizerTool;
